// Glossary data for finance & insurance terms relevant to FINABLE's services.
// Each entry has a slug, term, short definition (teaser) and longer paragraphs.

export type GlossaryCategory =
  | "Altersvorsorge"
  | "Investments"
  | "Steuern"
  | "Versicherung"
  | "Förderungen"
  | "Vermögensaufbau";

export type GlossaryEntry = {
  slug: string;
  term: string;
  category: GlossaryCategory;
  short: string;
  paragraphs: string[];
  related?: string[]; // slugs
};

export const entries: GlossaryEntry[] = [
  // A
  {
    slug: "altersvorsorge",
    term: "Altersvorsorge",
    category: "Altersvorsorge",
    short:
      "Maßnahmen zum Aufbau eines Einkommens für die Zeit nach dem Erwerbsleben – staatlich, betrieblich oder privat.",
    paragraphs: [
      "Altersvorsorge bezeichnet alle finanziellen Maßnahmen, mit denen Du Deinen Lebensstandard im Ruhestand absicherst. In Deutschland besteht die Altersvorsorge klassisch aus drei Säulen: der gesetzlichen Rente, der betrieblichen Altersvorsorge (bAV) und der privaten Vorsorge (z. B. Rürup, ETF-Sparpläne, Fondspolicen).",
      "Da die gesetzliche Rente in Zukunft eine wachsende Versorgungslücke aufweist, wird die private und betriebliche Altersvorsorge zunehmend wichtiger. Eine gute Strategie kombiniert mehrere Bausteine, nutzt steuerliche Vorteile und ist auf Deine individuelle Lebensplanung zugeschnitten.",
    ],
    related: ["rentenluecke", "ruerup-rente", "betriebliche-altersvorsorge", "etf"],
  },
  {
    slug: "abgeltungssteuer",
    term: "Abgeltungssteuer",
    category: "Steuern",
    short:
      "Pauschale Steuer von 25 % auf Kapitalerträge wie Zinsen, Dividenden und Kursgewinne – plus Soli und ggf. Kirchensteuer.",
    paragraphs: [
      "Die Abgeltungssteuer beträgt in Deutschland 25 % auf Kapitalerträge. Hinzu kommen 5,5 % Solidaritätszuschlag auf die Steuer sowie ggf. Kirchensteuer. Damit liegt die effektive Belastung bei rund 26,4 % bzw. 27,8 % bis 27,99 %.",
      "Sie wird in der Regel direkt von der depotführenden Bank abgeführt. Über den Sparerpauschbetrag (1.000 € pro Person, 2.000 € bei Verheirateten) bleiben Erträge bis zu dieser Höhe steuerfrei, sofern ein Freistellungsauftrag erteilt wurde.",
    ],
    related: ["freistellungsauftrag", "vorabpauschale", "etf", "sparerpauschbetrag", "kapitalertragsteuer", "nettorendite", "steuerabzug"],
  },
  {
    slug: "anlagehorizont",
    term: "Anlagehorizont",
    category: "Investments",
    short: "Geplanter Zeitraum, über den Kapital investiert bleibt – entscheidend für die Wahl der Anlageklasse.",
    paragraphs: [
      "Der Anlagehorizont beschreibt die Zeit, über die Du Dein Geld investieren kannst, ohne darauf zugreifen zu müssen. Je länger der Horizont, desto eher kannst Du Schwankungen aussitzen und in renditestarke Anlagen wie Aktien oder ETFs investieren.",
      "Als Faustregel gelten: kurzfristig (< 3 Jahre) → Tagesgeld/Festgeld, mittelfristig (3–7 Jahre) → Mischportfolio, langfristig (> 10 Jahre) → Aktien/ETFs. Ein klar definierter Anlagehorizont ist Grundlage jeder Anlagestrategie.",
    ],
    related: ["etf", "sparplan", "risikoprofil", "cost-average-effekt", "einmalanlage"],
  },

  // B
  {
    slug: "betriebliche-altersvorsorge",
    term: "Betriebliche Altersvorsorge (bAV)",
    category: "Altersvorsorge",
    short:
      "Vom Arbeitgeber organisierte Altersvorsorge – mit Steuer- und Sozialabgabenersparnis durch Entgeltumwandlung.",
    paragraphs: [
      "Die betriebliche Altersvorsorge (bAV) ist eine vom Arbeitgeber angebotene Form der Vorsorge. Beiträge werden meist per Entgeltumwandlung aus dem Bruttogehalt eingezahlt – das spart Steuern und Sozialabgaben in der Ansparphase.",
      "Seit 2019 ist der Arbeitgeber verpflichtet, mindestens 15 % Zuschuss zu zahlen, wenn er Sozialabgaben spart. In der Auszahlungsphase sind die Renten allerdings voll steuer- und krankenversicherungspflichtig. Eine wirtschaftliche Prüfung ist daher wichtig.",
    ],
    related: ["altersvorsorge", "ruerup-rente", "entgeltumwandlung", "direktversicherung", "hinterbliebenenversorgung"],
  },
  {
    slug: "berufsunfaehigkeitsversicherung",
    term: "Berufsunfähigkeitsversicherung",
    category: "Versicherung",
    short:
      "Zahlt eine monatliche Rente, wenn Du Deinen zuletzt ausgeübten Beruf aus gesundheitlichen Gründen nicht mehr ausüben kannst.",
    paragraphs: [
      "Die Berufsunfähigkeitsversicherung ist eine der wichtigsten Absicherungen für Erwerbstätige. Sie zahlt eine vereinbarte Rente, sobald Du zu mindestens 50 % berufsunfähig bist – meist auf Basis ärztlicher Gutachten.",
      "Wichtig sind faire Vertragsbedingungen, der Verzicht auf abstrakte Verweisung sowie eine ausreichende Rentenhöhe (Faustregel: 60–80 % des Nettoeinkommens). Je früher der Abschluss, desto günstiger der Beitrag und besser der Gesundheitszustand.",
    ],
    related: ["risikolebensversicherung", "grundfaehigkeitsversicherung", "krankentagegeld", "erwerbsminderungsrente", "aussteuerung"],
  },

  // D
  {
    slug: "diversifikation",
    term: "Diversifikation",
    category: "Investments",
    short:
      "Risikostreuung über verschiedene Anlageklassen, Regionen und Branchen, um Klumpenrisiken zu reduzieren.",
    paragraphs: [
      "Diversifikation ist das Streuen von Investments über verschiedene Wertpapiere, Anlageklassen, Regionen und Branchen. Ziel ist es, das Gesamtrisiko zu senken, ohne die erwartete Rendite stark zu reduzieren.",
      "Ein global gestreuter ETF auf den MSCI World oder MSCI ACWI investiert in über 1.500 bzw. 2.900 Unternehmen weltweit – ein einfaches und kostengünstiges Diversifikationsinstrument.",
    ],
    related: ["etf", "msci-world", "anlagehorizont"],
  },
  {
    slug: "dividende",
    term: "Dividende",
    category: "Investments",
    short: "Anteil am Unternehmensgewinn, der an Aktionäre ausgeschüttet wird.",
    paragraphs: [
      "Die Dividende ist der Teil des Gewinns, den eine Aktiengesellschaft an ihre Aktionäre ausschüttet. Sie wird i. d. R. einmal jährlich beschlossen und gilt als Indikator für ein gesundes Geschäftsmodell.",
      "Dividendenstrategien zielen darauf ab, ein passives Einkommen aufzubauen. Steuerlich gilt: Dividenden unterliegen in Deutschland der Abgeltungssteuer (25 % + Soli + ggf. Kirchensteuer).",
    ],
    related: ["abgeltungssteuer", "etf", "aktie", "sparerpauschbetrag", "depot", "kapitalertragsteuer"],
  },

  // E
  {
    slug: "etf",
    term: "ETF (Exchange Traded Fund)",
    category: "Investments",
    short:
      "Börsengehandelter Indexfonds – breit gestreut, kostengünstig und transparent. Kern moderner Vermögensanlage.",
    paragraphs: [
      "Ein ETF (Exchange Traded Fund) ist ein börsengehandelter Indexfonds, der einen Index wie den MSCI World oder DAX 1:1 abbildet. ETFs sind kostengünstig (Verwaltungsgebühr meist 0,05–0,5 % p. a.), transparent und einfach handelbar.",
      "Sie eignen sich für Sparpläne ab kleinen Beträgen, sind langfristig renditestark und gehören zu den wichtigsten Bausteinen moderner Vermögensanlage. ETFs sind als Sondervermögen vor Insolvenz der Fondsgesellschaft geschützt.",
    ],
    related: ["msci-world", "diversifikation", "sparplan", "vorabpauschale", "depot", "rendite", "cost-average-effekt", "musterportfolio", "investmentfonds", "wkn"],
  },
  {
    slug: "entgeltumwandlung",
    term: "Entgeltumwandlung",
    category: "Altersvorsorge",
    short:
      "Umwandlung von Bruttogehalt in Beiträge zur betrieblichen Altersvorsorge – steuer- und sozialabgabenfrei in der Ansparphase.",
    paragraphs: [
      "Bei der Entgeltumwandlung verzichtest Du auf einen Teil Deines Bruttogehalts zugunsten von Beiträgen in eine betriebliche Altersvorsorge. Bis zu 8 % der Beitragsbemessungsgrenze der allgemeinen Rentenversicherung (2026: bundesweit einheitlich 8.450 € pro Monat, also bis zu 676 € pro Monat) sind nach § 3 Nr. 63 EStG steuerfrei. Innerhalb dieses Rahmens sind die ersten 4 % (2026: 338 € pro Monat) zusätzlich beitragsfrei in der Sozialversicherung (§ 1 SvEV) – die beiden Grenzen liegen also übereinander, nicht nebeneinander.",
      "Das senkt die Abgabenlast in der Ansparphase deutlich. In der Rentenphase werden die Auszahlungen jedoch voll mit Einkommensteuer und Krankenkassenbeiträgen belastet (nachgelagerte Besteuerung).",
    ],
    related: ["betriebliche-altersvorsorge", "altersvorsorge", "direktversicherung"],
  },

  // F
  {
    slug: "freistellungsauftrag",
    term: "Freistellungsauftrag",
    category: "Steuern",
    short:
      "Erteilst Du Deiner Bank, damit Kapitalerträge bis 1.000 € (Single) bzw. 2.000 € (Verheiratet) steuerfrei bleiben.",
    paragraphs: [
      "Mit einem Freistellungsauftrag stellst Du sicher, dass Deine Bank Kapitalerträge bis zur Höhe des Sparerpauschbetrags steuerfrei auszahlt. Ohne Auftrag wird automatisch Abgeltungssteuer einbehalten.",
      "Der Auftrag kann auf mehrere Banken verteilt werden – die Summe darf den Pauschbetrag jedoch nicht überschreiten. Ein bestehender Auftrag sollte regelmäßig überprüft und bei Bedarf angepasst werden.",
    ],
    related: ["abgeltungssteuer", "vorabpauschale", "sparerpauschbetrag", "kapitalertragsteuer", "steuerabzug"],
  },
  {
    slug: "fondspolice",
    term: "Fondspolice",
    category: "Altersvorsorge",
    short:
      "Fondsgebundene Renten- oder Lebensversicherung mit Steuervorteilen in der Auszahlungsphase.",
    paragraphs: [
      "Eine Fondspolice ist eine Renten- oder Lebensversicherung, deren Beiträge in Investmentfonds oder ETFs investiert werden. Sie kombiniert Renditechancen am Kapitalmarkt mit den steuerlichen Vorteilen einer Versicherung.",
      "Bei Auszahlung nach dem 62. Lebensjahr und mindestens 12 Jahren Laufzeit greift das Halbeinkünfteverfahren – nur die Hälfte des Gewinns wird mit dem persönlichen Steuersatz besteuert. Wichtig sind niedrige Kosten und flexible Fondsauswahl.",
    ],
    related: ["altersvorsorge", "etf", "abgeltungssteuer"],
  },

  // G
  {
    slug: "grundfaehigkeitsversicherung",
    term: "Grundfähigkeitsversicherung",
    category: "Versicherung",
    short:
      "Zahlt eine Rente bei Verlust definierter Grundfähigkeiten wie Sehen, Gehen oder Greifen.",
    paragraphs: [
      "Die Grundfähigkeitsversicherung leistet eine Rente, wenn Du eine oder mehrere der versicherten Grundfähigkeiten dauerhaft verlierst – z. B. Sehen, Sprechen, Treppensteigen oder Autofahren. Sie ist eine sinnvolle Alternative oder Ergänzung zur Berufsunfähigkeitsversicherung.",
      "Vorteile: günstigere Beiträge, vereinfachte Gesundheitsprüfung. Nachteil: leistet nur bei klar definierten Fähigkeitsverlusten, nicht bei psychischen Erkrankungen.",
    ],
    related: ["berufsunfaehigkeitsversicherung"],
  },
  {
    slug: "gesetzliche-rente",
    term: "Gesetzliche Rentenversicherung",
    category: "Altersvorsorge",
    short:
      "Erste Säule der Altersvorsorge – Pflichtsystem für Arbeitnehmer, finanziert über das Umlageverfahren.",
    paragraphs: [
      "Die gesetzliche Rentenversicherung ist die wichtigste Säule der Altersvorsorge in Deutschland. Sie wird über das Umlageverfahren finanziert: Die Beiträge der heute Erwerbstätigen finanzieren die heutigen Renten.",
      "Aufgrund des demografischen Wandels sinkt das Rentenniveau zunehmend. Um den Lebensstandard zu sichern, ist eine ergänzende private oder betriebliche Altersvorsorge nahezu unumgänglich.",
    ],
    related: ["altersvorsorge", "rentenluecke", "ruerup-rente"],
  },

  // H
  {
    slug: "halbeinkuenfteverfahren",
    term: "Halbeinkünfteverfahren",
    category: "Steuern",
    short:
      "Bei Auszahlung von Fondspolicen nach 62 und 12 Jahren wird nur die Hälfte des Gewinns mit dem persönlichen Steuersatz versteuert.",
    paragraphs: [
      "Das Halbeinkünfteverfahren (auch 12/62-Regel) ist ein Steuervorteil bei Kapitallebens- und Rentenversicherungen. Voraussetzung: Auszahlung erst nach Vollendung des 62. Lebensjahres und mindestens 12 Jahren Vertragslaufzeit.",
      "In diesem Fall wird nur die Hälfte des Wertzuwachses mit dem persönlichen Einkommensteuersatz belastet – häufig günstiger als die pauschale Abgeltungssteuer.",
    ],
    related: ["fondspolice", "abgeltungssteuer", "altersvorsorge"],
  },

  // I
  {
    slug: "inflation",
    term: "Inflation",
    category: "Investments",
    short:
      "Kaufkraftverlust des Geldes durch steigende Preise – größter Risikofaktor für klassische Sparer.",
    paragraphs: [
      "Inflation bezeichnet den anhaltenden Anstieg des allgemeinen Preisniveaus. Eine Inflation von 3 % halbiert die Kaufkraft Deines Geldes in rund 24 Jahren.",
      "Sparer verlieren real Geld, wenn Zinsen unter der Inflation liegen. Sachwerte wie Aktien, ETFs, Immobilien und Gold gelten als Inflationsschutz, da sie langfristig mit oder über der Inflation wachsen.",
    ],
    related: ["etf", "sachwerte", "anlagehorizont", "inflationsrate", "zinswende"],
  },

  // K
  {
    slug: "kapitalertragsteuer",
    term: "Kapitalertragsteuer",
    category: "Steuern",
    short: "Sammelbegriff für Steuern auf Kapitalerträge – in Deutschland heute weitgehend gleich der Abgeltungssteuer.",
    paragraphs: [
      "Die Kapitalertragsteuer wird auf Erträge aus Kapitalvermögen erhoben – etwa Zinsen, Dividenden und Veräußerungsgewinne. Sie wird i. d. R. direkt von der Bank an das Finanzamt abgeführt.",
      "Seit 2009 erfolgt die Besteuerung pauschal mit 25 % (Abgeltungssteuer) zzgl. Soli und ggf. Kirchensteuer. Eine Günstigerprüfung in der Steuererklärung kann sich lohnen, wenn Dein persönlicher Steuersatz unter 25 % liegt.",
    ],
    related: ["abgeltungssteuer", "freistellungsauftrag", "sparerpauschbetrag", "solidaritaetszuschlag", "steuerabzug", "vermoegensteuer"],
  },

  // L
  {
    slug: "lebensversicherung",
    term: "Lebensversicherung",
    category: "Versicherung",
    short:
      "Sammelbegriff für Versicherungen, die im Todes- oder Erlebensfall leisten – als Risikoschutz oder Sparform.",
    paragraphs: [
      "Lebensversicherungen lassen sich in zwei Hauptformen unterteilen: die Risikolebensversicherung (reine Todesfallabsicherung) und die Kapitallebens- bzw. Rentenversicherung (Sparform mit Auszahlung im Erlebensfall).",
      "Klassische Kapitallebensversicherungen sind heute aufgrund niedriger Garantiezinsen kaum noch attraktiv. Fondsgebundene Varianten bieten bessere Renditechancen, sollten aber kostentransparent geprüft werden.",
    ],
    related: ["risikolebensversicherung", "fondspolice", "deckungskapital", "garantiezins", "ueberschussbeteiligung"],
  },

  // M
  {
    slug: "msci-world",
    term: "MSCI World",
    category: "Investments",
    short:
      "Aktienindex mit über 1.500 Unternehmen aus 23 Industrieländern – beliebter Basisindex für ETF-Anleger.",
    paragraphs: [
      "Der MSCI World ist ein globaler Aktienindex, der die Wertentwicklung von rund 1.500 großen und mittelgroßen Unternehmen aus 23 Industrieländern abbildet. Die USA dominieren mit etwa 70 % Anteil.",
      "ETFs auf den MSCI World gelten als Standardbaustein für langfristigen Vermögensaufbau – breit diversifiziert, kostengünstig und liquide. Wer auch Schwellenländer abdecken will, ergänzt mit MSCI Emerging Markets oder nimmt direkt den MSCI ACWI.",
    ],
    related: ["etf", "diversifikation", "sparplan", "depot", "rendite", "benchmark", "marktkapitalisierung"],
  },

  // N
  {
    slug: "nachgelagerte-besteuerung",
    term: "Nachgelagerte Besteuerung",
    category: "Steuern",
    short:
      "Beiträge sind in der Ansparphase steuerlich begünstigt, dafür werden Auszahlungen im Alter voll besteuert.",
    paragraphs: [
      "Bei der nachgelagerten Besteuerung sind Vorsorgebeiträge in der Ansparphase ganz oder teilweise von der Steuer befreit. Im Gegenzug werden die Auszahlungen im Rentenalter vollständig oder anteilig der Einkommensteuer unterworfen.",
      "Dieses Prinzip gilt z. B. für die gesetzliche Rente, Rürup-Rente und betriebliche Altersvorsorge. Da der Steuersatz im Alter meist niedriger ist als im Berufsleben, ergibt sich i. d. R. ein Steuervorteil.",
    ],
    related: ["ruerup-rente", "betriebliche-altersvorsorge", "gesetzliche-rente", "steuerstundung"],
  },

  // P
  {
    slug: "pflegeversicherung",
    term: "Pflegeversicherung",
    category: "Versicherung",
    short:
      "Sichert das Pflegerisiko ab – die gesetzliche Versicherung deckt nur einen Teil der tatsächlichen Kosten.",
    paragraphs: [
      "Die gesetzliche Pflegeversicherung leistet abhängig vom Pflegegrad nur einen Teilbetrag der tatsächlichen Pflegekosten. Die Differenz – oft mehrere Tausend Euro pro Monat – muss aus eigenem Vermögen oder von Angehörigen getragen werden.",
      "Eine private Pflegezusatzversicherung schließt diese Lücke. Es gibt drei Varianten: Pflegetagegeld, Pflegekosten- und Pflegerentenversicherung. Welche passt, hängt von Familiensituation und Vermögen ab.",
    ],
    related: ["versicherungslucke", "berufsunfaehigkeitsversicherung", "langzeitpflege", "pflegegrad"],
  },
  {
    slug: "private-krankenversicherung",
    term: "Private Krankenversicherung (PKV)",
    category: "Versicherung",
    short:
      "Alternative zur gesetzlichen Krankenversicherung für Beamte, Selbstständige und Gutverdiener mit individuellen Tarifen.",
    paragraphs: [
      "Die private Krankenversicherung (PKV) bietet leistungsstarke, individuell wählbare Tarife – steht aber nur Beamten, Selbstständigen und Angestellten oberhalb der Versicherungspflichtgrenze offen (2026: 77.400 € brutto pro Jahr, Sozialversicherungsrechengrößen-Verordnung 2026, BGBl. 2025 I Nr. 278).",
      "Vorteile sind oft bessere medizinische Leistungen und kürzere Wartezeiten. Nachteile: Beiträge können im Alter stark steigen, kein kostenloser Familienangehörigenmitversicherung. Ein Wechsel will gut überlegt sein.",
    ],
    related: ["versicherungslucke", "pflegeversicherung", "krankentagegeld"],
  },

  // R
  {
    slug: "rentenluecke",
    term: "Rentenlücke",
    category: "Altersvorsorge",
    short:
      "Differenz zwischen gewünschtem Einkommen im Ruhestand und der zu erwartenden gesetzlichen Rente.",
    paragraphs: [
      "Die Rentenlücke beschreibt den Betrag, der Dir im Ruhestand zu Deinem gewünschten Lebensstandard fehlt. Sie entsteht, weil die gesetzliche Rente nur etwa 48 % des Durchschnittseinkommens ersetzt – bei steigender Inflation sinkt die reale Kaufkraft zusätzlich.",
      "Eine ehrliche Hochrechnung zeigt oft Lücken von mehreren Hundert Euro pro Monat. Sie lässt sich über private und betriebliche Vorsorge schließen. Je früher Du beginnst, desto kleiner sind die nötigen monatlichen Beiträge.",
    ],
    related: ["altersvorsorge", "gesetzliche-rente", "ruerup-rente", "betriebliche-altersvorsorge"],
  },
  {
    slug: "ruerup-rente",
    term: "Rürup-Rente (Basisrente)",
    category: "Altersvorsorge",
    short:
      "Steuerlich geförderte private Altersvorsorge – besonders attraktiv für Selbstständige und Gutverdiener.",
    paragraphs: [
      "Die Rürup-Rente (auch Basisrente) ist eine private Altersvorsorge mit hoher steuerlicher Förderung. Beiträge sind 2026 bis 30.826 € (Single) bzw. 61.652 € (Verheiratete, Zusammenveranlagung) zu 100 % als Sonderausgaben absetzbar (§ 10 Abs. 1 Nr. 2 EStG).",
      "Im Gegenzug ist die Rente lebenslang und kann nicht vorzeitig ausgezahlt, beliehen oder vererbt werden (außer an Ehepartner und Kinder). Besonders Selbstständige ohne gesetzliche Rente und Gutverdiener mit hoher Steuerlast profitieren stark.",
    ],
    related: ["altersvorsorge", "nachgelagerte-besteuerung", "betriebliche-altersvorsorge", "basisrente", "steuerstundung"],
  },
  {
    slug: "riester-rente",
    term: "Riester-Rente",
    category: "Altersvorsorge",
    short:
      "Staatlich geförderte private Altersvorsorge mit Zulagen und Steuervorteilen – v. a. für Familien attraktiv.",
    paragraphs: [
      "Die Riester-Rente ist eine staatlich geförderte private Altersvorsorge. Sie kombiniert Zulagen (175 € Grundzulage, 300 € pro Kind) mit Steuervorteilen über den Sonderausgabenabzug.",
      "Sie lohnt sich vor allem für Familien mit Kindern und Geringverdiener. Durch hohe Kosten klassischer Tarife und Garantiepflichten ist die Riester-Rente in den letzten Jahren kritischer geworden – eine individuelle Prüfung ist Pflicht.",
      "Mit dem Altersvorsorge-Reformgesetz (BGBl. I 2026 Nr. 156) endet zum 31. Dezember 2026 die Möglichkeit, neue Riester-Verträge — inklusive Wohn-Riester — abzuschließen. Ab 1. Januar 2027 löst das Altersvorsorgedepot die Riester-Rente als staatlich gefördertes Vorsorgeprodukt für Neuverträge ab. Bestehende Riester-Verträge laufen unverändert weiter und werden weitergefördert; Details siehe Eintrag zum Altersvorsorgedepot.",
    ],
    related: ["altersvorsorge", "ruerup-rente", "foerderungen", "altersvorsorgedepot", "garantiezins"],
  },
  {
    slug: "risikolebensversicherung",
    term: "Risikolebensversicherung",
    category: "Versicherung",
    short:
      "Zahlt eine vereinbarte Summe an Hinterbliebene im Todesfall – wichtig für Familien und Kreditnehmer.",
    paragraphs: [
      "Die Risikolebensversicherung leistet ausschließlich im Todesfall der versicherten Person eine vorab vereinbarte Summe an die Begünstigten. Sie ist eine reine Risikoabsicherung ohne Sparanteil und daher sehr günstig.",
      "Sie ist essenziell für Familien mit Kindern, junge Paare nach dem Hauskauf oder Geschäftspartner. Faustregel: Versicherungssumme = 5–7-faches Bruttojahreseinkommen oder Höhe der Restschuld + Zukunftsbedarf.",
    ],
    related: ["lebensversicherung", "berufsunfaehigkeitsversicherung", "hinterbliebenenversorgung"],
  },
  {
    slug: "risikoprofil",
    term: "Risikoprofil",
    category: "Investments",
    short:
      "Persönliche Bereitschaft und Fähigkeit, Schwankungen und mögliche Verluste in der Geldanlage zu tragen.",
    paragraphs: [
      "Das Risikoprofil beschreibt, wie viel Schwankung und potenziellen Verlust Du als Anleger emotional und finanziell verkraften kannst. Es ist Grundlage jeder seriösen Anlageempfehlung.",
      "Faktoren sind u. a. Anlagehorizont, Vermögenssituation, Erfahrung und persönliche Risikoneigung. Ein zu offensives Profil führt zu Panikverkäufen, ein zu defensives zu unzureichender Rendite.",
    ],
    related: ["anlagehorizont", "diversifikation", "etf", "transparente-finanzberatung"],
  },

  // S
  {
    slug: "sparerpauschbetrag",
    term: "Sparerpauschbetrag",
    category: "Steuern",
    short:
      "Steuerfreibetrag auf Kapitalerträge: 1.000 € pro Person, 2.000 € bei Verheirateten (seit 2023, unverändert 2026).",
    paragraphs: [
      "Der Sparerpauschbetrag ist ein jährlicher Freibetrag, bis zu dem Kapitalerträge steuerfrei bleiben. Seit 2023 beträgt er 1.000 € pro Person bzw. 2.000 € für gemeinsam veranlagte Ehepaare – 2026 unverändert.",
      "Voraussetzung für die automatische Berücksichtigung ist ein Freistellungsauftrag bei der Bank. Wird er nicht erteilt, kann der Betrag über die Steuererklärung zurückgeholt werden.",
    ],
    related: ["freistellungsauftrag", "abgeltungssteuer", "kapitalertragsteuer", "vorabpauschale"],
  },
  {
    slug: "sparplan",
    term: "Sparplan",
    category: "Vermögensaufbau",
    short:
      "Regelmäßige Investition fester Beträge – nutzt den Cost-Average-Effekt und baut langfristig Vermögen auf.",
    paragraphs: [
      "Ein Sparplan ist eine automatische, regelmäßige Investition (z. B. monatlich) in einen ETF, Fonds oder eine Versicherung. Bereits ab 25 € pro Monat möglich, ist er der Klassiker für den langfristigen Vermögensaufbau.",
      "Vorteil ist der Cost-Average-Effekt: Bei niedrigen Kursen werden mehr Anteile gekauft, bei hohen weniger. Über lange Zeiträume gleichen sich Marktschwankungen aus und der Zinseszinseffekt entfaltet seine volle Wirkung.",
    ],
    related: ["etf", "msci-world", "zinseszinseffekt", "depot", "rendite", "cost-average-effekt", "notfallreserve", "einmalanlage", "fire-methode"],
  },
  {
    slug: "sondervermoegen",
    term: "Sondervermögen",
    category: "Investments",
    short:
      "Fondsvermögen wird getrennt vom Vermögen der Fondsgesellschaft verwahrt – Schutz im Insolvenzfall.",
    paragraphs: [
      "Investmentfonds und ETFs gelten in Deutschland als Sondervermögen. Das bedeutet: Das angelegte Geld der Anleger wird getrennt vom Vermögen der Kapitalverwaltungsgesellschaft bei einer Depotbank verwahrt.",
      "Sollte die Fondsgesellschaft insolvent werden, bleiben die Anteile der Anleger geschützt und sind nicht Teil der Insolvenzmasse. Das macht ETFs zu einer besonders sicheren Anlageform – im Gegensatz z. B. zu Zertifikaten.",
    ],
    related: ["etf", "diversifikation", "investmentfonds"],
  },
  {
    slug: "sachwerte",
    term: "Sachwerte",
    category: "Investments",
    short:
      "Reale Vermögenswerte wie Aktien, Immobilien oder Gold – langfristig Schutz vor Inflation.",
    paragraphs: [
      "Sachwerte sind reale, physische oder unternehmerische Vermögenswerte – im Gegensatz zu Geldwerten wie Sparguthaben oder Anleihen. Beispiele sind Aktien, Immobilien, Gold und Rohstoffe.",
      "Sie gelten als Inflationsschutz, da ihre Preise langfristig mit oder über der Inflation steigen. Eine ausgewogene Mischung aus Sach- und Geldwerten ist Grundlage einer robusten Vermögensstrategie.",
    ],
    related: ["inflation", "etf", "diversifikation", "inflationsrate"],
  },

  // V
  {
    slug: "vorabpauschale",
    term: "Vorabpauschale",
    category: "Steuern",
    short:
      "Jährliche Vorabbesteuerung thesaurierender Fonds und ETFs – wird auf den späteren Verkaufsgewinn angerechnet.",
    paragraphs: [
      "Die Vorabpauschale ist eine jährliche pauschale Besteuerung thesaurierender ETFs und Fonds, eingeführt mit der Investmentsteuerreform 2018. Sie wird zu Jahresbeginn auf Basis des Basiszinses berechnet und im Januar fällig.",
      "Wichtig: Die Vorabpauschale wird beim späteren Verkauf der Anteile auf die Steuerlast angerechnet – es entsteht also keine Doppelbesteuerung. Liegt zu Jahresbeginn ausreichend Guthaben auf dem Verrechnungskonto, läuft die Abbuchung automatisch.",
    ],
    related: ["abgeltungssteuer", "etf", "freistellungsauftrag", "sparerpauschbetrag"],
  },
  {
    slug: "versicherungslucke",
    term: "Versorgungslücke",
    category: "Altersvorsorge",
    short:
      "Differenz zwischen abgesichertem Bedarf und tatsächlich vorhandenen Leistungen – etwa im Alter, bei Berufsunfähigkeit oder Pflege.",
    paragraphs: [
      "Eine Versorgungslücke entsteht, wenn die staatlichen oder gesetzlichen Leistungen nicht ausreichen, um den gewünschten Lebensstandard zu sichern – z. B. im Alter, bei Berufsunfähigkeit oder Pflegebedürftigkeit.",
      "Eine fundierte Bedarfsanalyse zeigt, wo Lücken bestehen und mit welchen Bausteinen sie effizient geschlossen werden können – idealerweise unabhängig und produktneutral.",
    ],
    related: ["rentenluecke", "berufsunfaehigkeitsversicherung", "pflegeversicherung", "transparente-finanzberatung"],
  },
  {
    slug: "vermoegenswirksame-leistungen",
    term: "Vermögenswirksame Leistungen (VL)",
    category: "Förderungen",
    short:
      "Geldleistung des Arbeitgebers zum Vermögensaufbau – staatlich gefördert über Arbeitnehmer-Sparzulage und Wohnungsbauprämie.",
    paragraphs: [
      "Vermögenswirksame Leistungen (VL) sind eine zusätzliche Geldleistung des Arbeitgebers, die in einen geförderten Vertrag fließt – z. B. ETF-Sparplan, Bausparvertrag oder Banksparplan. Bis zu 40 € monatlich sind üblich.",
      "Bei Einhaltung von Einkommensgrenzen gewährt der Staat zusätzlich die Arbeitnehmer-Sparzulage. Insbesondere ETF-Sparpläne als VL sind aktuell renditestark und sollten von jedem Arbeitnehmer geprüft werden.",
    ],
    related: ["sparplan", "etf", "foerderungen"],
  },

  // Z
  {
    slug: "zinseszinseffekt",
    term: "Zinseszinseffekt",
    category: "Vermögensaufbau",
    short:
      "Erträge werden mitverzinst – langfristig der wichtigste Hebel beim Vermögensaufbau.",
    paragraphs: [
      "Der Zinseszinseffekt beschreibt das exponentielle Wachstum von Kapital, wenn nicht nur das eingesetzte Geld, sondern auch die bereits erzielten Erträge weiter verzinst werden.",
      "Beispiel: 100 € monatlich, 7 % Rendite p. a., 30 Jahre Laufzeit – ergeben rund 122.000 €, davon mehr als 86.000 € allein durch Zinseszinsen. Wer früh anfängt und konsequent investiert, profitiert am stärksten.",
    ],
    related: ["sparplan", "etf", "altersvorsorge", "opportunitaetskosten"],
  },
  {
    slug: "foerderungen",
    term: "Staatliche Förderungen",
    category: "Förderungen",
    short:
      "Zulagen, Steuervorteile und Zuschüsse – lassen sich gezielt nutzen, um Vermögen schneller aufzubauen.",
    paragraphs: [
      "Der Staat fördert Vermögensaufbau und Vorsorge über zahlreiche Programme: Riester-Zulagen, Rürup-Steuervorteile, Wohnungsbauprämie, Arbeitnehmer-Sparzulage, BAföG-Sparen, Wohn-Riester u. v. m.",
      "Eine systematische Prüfung der individuellen Anspruchsvoraussetzungen kann pro Jahr mehrere Hundert Euro extra bedeuten – Geld, das Du sonst verschenken würdest.",
    ],
    related: ["riester-rente", "ruerup-rente", "vermoegenswirksame-leistungen"],
  },

  // ===== Added: extended glossary =====

  {
    slug: "altersvorsorgedepot",
    term: "Altersvorsorgedepot",
    category: "Altersvorsorge",
    short:
      "Ab 2027 verfügbares staatlich gefördertes Wertpapierdepot – der Nachfolger der Riester-Rente, erstmals mit ETFs und für Selbstständige.",
    paragraphs: [
      "Das Altersvorsorgedepot ist ein ab 2027 geplantes staatlich gefördertes Wertpapierdepot für die private Altersvorsorge. Erstmals kannst Du ETFs und Fonds direkt mit staatlichen Zulagen besparen – ohne die starre Beitragsgarantie der Riester-Rente.",
      "Förderberechtigt sind alle Arbeitnehmer, Beamte und – neu – auch Selbstständige. Damit schließt das Altersvorsorgedepot eine wichtige Lücke und macht moderne ETF-basierte Vorsorge erstmals breit staatlich gefördert zugänglich.",
    ],
    related: ["riester-rente", "altersvorsorge", "etf"],
  },
  {
    slug: "arbeitnehmer-sparzulage",
    term: "Arbeitnehmer-Sparzulage",
    category: "Förderungen",
    short:
      "Staatliche Förderung auf vermögenswirksame Leistungen — 20 % auf Wertpapier-VL (max. 80 €/Jahr) und 9 % auf Bauspar-VL (max. 42,30 €/Jahr), § 13 5. VermBG.",
    paragraphs: [
      "Die Arbeitnehmer-Sparzulage ist eine staatliche Förderung auf vermögenswirksame Leistungen (VL). Rechtsgrundlage ist § 13 des Fünften Vermögensbildungsgesetzes (5. VermBG). Die Höhe hängt von der Anlageform ab: Für VL in Wertpapieren (z. B. Aktienfonds, ETF-Sparpläne) beträgt sie 20 % auf bis zu 400 € pro Jahr — also maximal 80 € Zulage. Für VL im Bausparen oder in wohnungswirtschaftlicher Verwendung beträgt sie 9 % auf bis zu 470 € pro Jahr — also maximal 42,30 € Zulage.",
      "Die Einkommensgrenze ist seit 2024 für beide Anlageformen einheitlich: 40.000 € (Ledige) bzw. 80.000 € (Zusammenveranlagte) zu versteuerndes Einkommen im Anlagejahr. Beantragt wird die Zulage über die Anlage VL in der Einkommensteuererklärung; ausgezahlt wird am Ende der siebenjährigen Sperrfrist des VL-Vertrags.",
    ],
    related: ["vermoegenswirksame-leistungen", "foerderungen"],
  },
  {
    slug: "ausschuettend",
    term: "Ausschüttend",
    category: "Investments",
    short:
      "Fondstyp, bei dem Dividenden und Zinsen regelmäßig ausgezahlt werden – Gegenstück zu thesaurierenden Fonds.",
    paragraphs: [
      "Ausschüttende Fonds und ETFs zahlen Dividenden und Zinserträge regelmäßig auf Dein Verrechnungskonto aus – meist quartalsweise, halbjährlich oder einmal pro Jahr.",
      "Sie sind besonders attraktiv, wenn Du ein laufendes passives Einkommen aufbauen oder regelmäßig vom Sparerpauschbetrag profitieren möchtest. Für reinen Vermögensaufbau ist die thesaurierende Variante oft steuerlich effizienter.",
    ],
    related: ["thesaurierend", "etf", "dividende"],
  },
  {
    slug: "depot",
    term: "Depot",
    category: "Investments",
    short:
      "Konto bei einer Bank oder einem Broker, auf dem Wertpapiere wie ETFs, Aktien und Fonds verwahrt werden.",
    paragraphs: [
      "Ein Depot ist die Grundvoraussetzung, um an der Börse in Wertpapiere zu investieren. Es funktioniert wie ein Konto, nur dass darauf keine Geldbeträge, sondern ETFs, Aktien und Fonds verwahrt werden.",
      "Die meisten Online-Broker bieten Depots heute kostenlos an. Wichtig sind ein gutes Sparplanangebot, niedrige Transaktionskosten und eine benutzerfreundliche Plattform.",
    ],
    related: ["etf", "sparplan", "sondervermoegen", "wertpapier", "depotverwaltung"],
  },
  {
    slug: "gesamtkostenquote",
    term: "Gesamtkostenquote (TER)",
    category: "Investments",
    short:
      "Jährliche Gesamtkostenquote eines Fonds oder ETFs in Prozent des angelegten Kapitals – zentral für die Rendite.",
    paragraphs: [
      "Die Total Expense Ratio (TER) zeigt, wie viel Prozent des angelegten Kapitals jährlich für Verwaltung und Betrieb eines Fonds oder ETFs abgezogen werden.",
      "Günstige Welt-ETFs liegen bei 0,1–0,2 % p. a., aktiv gemanagte Fonds oft bei 1,5–2,5 %. Über 30 Jahre Anlagedauer kann das einen Renditeunterschied von mehreren Zehntausend Euro ausmachen.",
    ],
    related: ["etf", "msci-world", "sparplan", "rendite", "depotverwaltung", "fondsmanager", "investmentfonds", "nettorendite"],
  },
  {
    slug: "grundrente",
    term: "Grundrente",
    category: "Altersvorsorge",
    short:
      "Staatliche Aufstockung der gesetzlichen Rente für Menschen mit langen Beitragszeiten und niedrigem Einkommen – seit 2021 automatisch.",
    paragraphs: [
      "Die Grundrente ist eine staatliche Aufstockung der gesetzlichen Rente für Menschen, die lange gearbeitet, aber unterdurchschnittlich verdient haben. Voraussetzung sind mindestens 33 Jahre Beitragszeiten.",
      "Seit 2021 wird sie automatisch von der Deutschen Rentenversicherung geprüft und ausgezahlt – ein gesonderter Antrag ist nicht nötig. Der Zuschlag liegt im Schnitt bei rund 75–80 € pro Monat.",
    ],
    related: ["gesetzliche-rente", "rentenluecke", "altersvorsorge"],
  },
  {
    slug: "kaufkraft",
    term: "Kaufkraft",
    category: "Investments",
    short:
      "Realer Wert des Geldes – wie viele Güter und Dienstleistungen Du für einen bestimmten Betrag kaufen kannst.",
    paragraphs: [
      "Die Kaufkraft beschreibt den realen Wert Deines Geldes. Sie sinkt durch Inflation kontinuierlich: Bei 3 % Inflation halbiert sich die Kaufkraft Deines Geldes in rund 24 Jahren.",
      "Um die Kaufkraft langfristig zu erhalten, musst Du Dein Geld so anlegen, dass die Rendite über der Inflationsrate liegt – etwa über breit gestreute ETFs, Aktien oder andere Sachwerte.",
    ],
    related: ["inflation", "sachwerte", "etf", "inflationsrate"],
  },
  {
    slug: "krankentagegeld",
    term: "Krankentagegeld",
    category: "Versicherung",
    short:
      "Versicherungsleistung, die Dein Einkommen absichert, wenn die Lohnfortzahlung nach 6 Wochen Krankheit endet.",
    paragraphs: [
      "Das Krankentagegeld springt ein, wenn Du länger als 6 Wochen krank bist und der Arbeitgeber die Lohnfortzahlung einstellt. Es schließt die Lücke zwischen gesetzlichem Krankengeld und Deinem Netto-Einkommen.",
      "Besonders wichtig ist es für Selbstständige, Freiberufler und Gutverdiener, deren Krankengeld der gesetzlichen Kasse oft deutlich unter dem gewohnten Lebensstandard liegt.",
    ],
    related: ["berufsunfaehigkeitsversicherung", "private-krankenversicherung", "krankengeld", "aussteuerung", "lohnfortzahlung"],
  },
  {
    slug: "nettolohn",
    term: "Nettolohn",
    category: "Steuern",
    short:
      "Tatsächlich ausgezahltes Gehalt nach Abzug von Steuern und Sozialabgaben vom Bruttolohn.",
    paragraphs: [
      "Der Nettolohn ist der Betrag, der nach Abzug von Lohnsteuer, Solidaritätszuschlag, Kirchensteuer und Sozialabgaben tatsächlich auf Deinem Konto landet.",
      "Je nach Steuerklasse und Einkommenshöhe beträgt die Differenz zwischen Brutto und Netto 30–45 %. Über Entgeltumwandlung, VL und Sachbezüge lässt sich der effektive Nettolohn spürbar erhöhen.",
    ],
    related: ["entgeltumwandlung", "vermoegenswirksame-leistungen", "werbungskosten"],
  },
  {
    slug: "progressionsvorbehalt",
    term: "Progressionsvorbehalt",
    category: "Steuern",
    short:
      "Erhöht Deinen Steuersatz, wenn Du steuerfreie Lohnersatzleistungen wie Elterngeld oder Kurzarbeitergeld bezogen hast.",
    paragraphs: [
      "Der Progressionsvorbehalt sorgt dafür, dass bestimmte steuerfreie Einnahmen (z. B. Elterngeld, Kurzarbeitergeld, Arbeitslosengeld I) bei der Berechnung Deines persönlichen Steuersatzes berücksichtigt werden.",
      "Die Lohnersatzleistungen selbst bleiben steuerfrei, erhöhen aber den Steuersatz auf Dein übriges Einkommen. Wer mehr als 410 € solcher Leistungen erhalten hat, ist zur Abgabe einer Steuererklärung verpflichtet.",
    ],
    related: ["nettolohn", "werbungskosten", "steuererklaerung", "kirchensteuer", "steuerprogression"],
  },
  {
    slug: "rebalancing",
    term: "Rebalancing",
    category: "Investments",
    short:
      "Regelmäßige Wiederherstellung der ursprünglichen Portfoliogewichtung, um das gewünschte Risikoprofil zu erhalten.",
    paragraphs: [
      "Rebalancing bezeichnet die regelmäßige Wiederherstellung der ursprünglichen Gewichtung Deines Portfolios. Steigen z. B. Aktien stärker als Anleihen, verschiebt sich Dein Risikoprofil – Rebalancing bringt es zurück in die Balance.",
      "In der Praxis reicht es, einmal jährlich oder bei Abweichungen von mehr als 5–10 % zu rebalancen. Steueroptimal ist es, die Umschichtung über neue Sparraten statt über Verkäufe zu lösen.",
    ],
    related: ["diversifikation", "risikoprofil", "sparplan", "musterportfolio"],
  },
  {
    slug: "rentenbescheid",
    term: "Rentenbescheid",
    category: "Altersvorsorge",
    short:
      "Offizielles Dokument der Deutschen Rentenversicherung, das Höhe und Berechnung Deiner Rente festlegt.",
    paragraphs: [
      "Der Rentenbescheid ist das offizielle Dokument der Deutschen Rentenversicherung, das die Höhe Deiner gewährten Rente festlegt und die Berechnungsgrundlagen erläutert – inklusive Entgeltpunkten, Rentenwert und Zugangsfaktor.",
      "Du solltest den Bescheid sorgfältig prüfen. Bei Fehlern hast Du einen Monat Zeit, schriftlich Widerspruch einzulegen – am besten mit Unterstützung einer unabhängigen Beratung.",
    ],
    related: ["gesetzliche-rente", "rentenluecke", "grundrente", "rentenversicherungsnummer", "wartezeit-rente"],
  },
  {
    slug: "sonderausgaben",
    term: "Sonderausgaben",
    category: "Steuern",
    short:
      "Private Ausgaben, die das Gesetz steuerlich begünstigt – z. B. Vorsorgeaufwendungen, Spenden und Kirchensteuer.",
    paragraphs: [
      "Sonderausgaben sind private Ausgaben, die Du in Deiner Steuererklärung absetzen kannst – z. B. Beiträge zu Rürup, Riester, Kranken- und Pflegeversicherung, Spenden, Kirchensteuer und Kinderbetreuungskosten.",
      "Der Pauschbetrag liegt bei mageren 36 € (Single) bzw. 72 € (Verheiratete). Wer höhere Sonderausgaben hat, sollte diese unbedingt einzeln nachweisen – das senkt die Steuerlast spürbar.",
    ],
    related: ["ruerup-rente", "riester-rente", "werbungskosten"],
  },
  {
    slug: "thesaurierend",
    term: "Thesaurierend",
    category: "Investments",
    short:
      "Fondstyp, bei dem Dividenden und Zinsen automatisch reinvestiert werden – maximiert den Zinseszinseffekt.",
    paragraphs: [
      "Bei thesaurierenden Fonds und ETFs werden Dividenden und Zinsen nicht ausgezahlt, sondern automatisch im Fonds reinvestiert. Dadurch wirkt der Zinseszinseffekt ohne Dein Zutun maximal.",
      "Für den langfristigen Vermögensaufbau sind thesaurierende ETFs in der Regel die effizienteste Wahl. Steuerlich erfolgt eine jährliche Vorabpauschale, die beim Verkauf angerechnet wird.",
    ],
    related: ["ausschuettend", "etf", "vorabpauschale", "zinseszinseffekt"],
  },
  {
    slug: "tagesgeld",
    term: "Tagesgeld",
    category: "Vermögensaufbau",
    short:
      "Flexible Geldanlage mit täglicher Verfügbarkeit und variablem Zins – ideal als Notfallreserve.",
    paragraphs: [
      "Tagesgeld ist eine flexible, täglich verfügbare Geldanlage bei einer Bank mit variablem Zinssatz und gesetzlicher Einlagensicherung bis 100.000 € pro Bank und Kunde.",
      "Es eignet sich hervorragend als Notreserve (3–6 Nettogehälter) – nicht aber als langfristige Geldanlage, da die Zinsen meist unter der Inflation liegen.",
    ],
    related: ["etf", "inflation", "sparplan", "notfallreserve"],
  },
  {
    slug: "werbungskosten",
    term: "Werbungskosten",
    category: "Steuern",
    short:
      "Beruflich bedingte Ausgaben, die von der Steuer absetzbar sind – Arbeitnehmer-Pauschbetrag 1.230 € (2026).",
    paragraphs: [
      "Werbungskosten sind Ausgaben, die im Zusammenhang mit Deinem Beruf stehen – etwa Fahrtkosten, Arbeitsmittel, Fortbildungen, Bewerbungskosten oder die Homeoffice-Pauschale.",
      "Der Arbeitnehmer-Pauschbetrag von 1.230 € (Stand 2026) wird automatisch berücksichtigt. Wer höhere Werbungskosten hat, sollte diese mit Belegen einzeln in der Steuererklärung angeben.",
    ],
    related: ["sonderausgaben", "nettolohn", "jobticket"],
  },
  {
    slug: "wohnungsbaupraemie",
    term: "Wohnungsbauprämie",
    category: "Förderungen",
    short:
      "Staatliche Förderung von 10 % auf Bauspareinlagen – bis zu 70 € jährlich für Singles mit niedrigem bis mittlerem Einkommen.",
    paragraphs: [
      "Die Wohnungsbauprämie ist eine staatliche Förderung von 10 % auf Bauspareinlagen für Personen mit niedrigem bis mittlerem Einkommen.",
      "Sie beträgt maximal 70 € pro Jahr (Singles) bzw. 140 € (Verheiratete). Berechtigt sind alle ab 16 Jahren mit einem zu versteuernden Einkommen bis 35.000 € (Single) bzw. 70.000 € (Verheiratete).",
    ],
    related: ["foerderungen", "vermoegenswirksame-leistungen"],
  },

  // ===== Added: extended glossary (batch 2) =====

  {
    slug: "aktie",
    term: "Aktie",
    category: "Investments",
    short:
      "Anteilsschein an einem Unternehmen – wer Aktien kauft, wird Miteigentümer und profitiert von Kursgewinnen und Dividenden.",
    paragraphs: [
      "Eine Aktie ist ein Anteilsschein an einem Unternehmen. Wer Aktien kauft, wird Miteigentümer und profitiert von Kursgewinnen und ggf. Dividenden – trägt aber auch das unternehmerische Risiko.",
      "Einzelaktien sind deutlich riskanter als breit gestreute ETFs, weil das Geld nur in ein Unternehmen fließt. Für die meisten Privatanleger sind ETFs daher der bessere Einstieg, mit Aktien als optionale Beimischung.",
    ],
    related: ["etf", "dividende", "diversifikation", "kapitalmarkt", "marktkapitalisierung", "wkn"],
  },
  {
    slug: "altersrente",
    term: "Altersrente",
    category: "Altersvorsorge",
    short:
      "Monatliche Zahlung aus der gesetzlichen Rentenversicherung ab Rentenalter – meist 67 Jahre, abhängig von Rentenpunkten.",
    paragraphs: [
      "Die Altersrente ist die monatliche Zahlung aus der gesetzlichen Rentenversicherung ab dem Rentenalter – für die meisten Jahrgänge 67 Jahre.",
      "Die Höhe hängt von den über das Berufsleben gesammelten Entgeltpunkten ab. Wer früher in Rente geht, muss Abschläge von 0,3 % pro Monat hinnehmen.",
    ],
    related: ["gesetzliche-rente", "rentenpunkte", "rentenluecke", "wartezeit-rente"],
  },
  {
    slug: "bafin",
    term: "BaFin",
    category: "Versicherung",
    short:
      "Bundesanstalt für Finanzdienstleistungsaufsicht – deutsche Aufsichtsbehörde für Banken, Versicherungen und Finanzdienstleister.",
    paragraphs: [
      "Die BaFin (Bundesanstalt für Finanzdienstleistungsaufsicht) ist die deutsche Aufsichtsbehörde für Banken, Versicherungen und Finanzdienstleister. Sie sorgt für Stabilität und Integrität am deutschen Finanzmarkt.",
      "Jeder seriöse Finanzberater muss bei der BaFin oder einer zuständigen Aufsicht registriert sein. Über das öffentliche BaFin-Register kannst Du das jederzeit prüfen.",
    ],
    related: ["private-krankenversicherung", "berufsunfaehigkeitsversicherung"],
  },
  {
    slug: "beitragsbemessungsgrenze",
    term: "Beitragsbemessungsgrenze",
    category: "Steuern",
    short:
      "Einkommensgrenze, bis zu der Sozialversicherungsbeiträge berechnet werden – 2026 bundeseinheitlich 8.450 €/Monat (RV/ALV) bzw. 5.812,50 €/Monat (GKV/PV).",
    paragraphs: [
      "Die Beitragsbemessungsgrenze (BBG) legt fest, bis zu welchem Bruttoeinkommen Sozialversicherungsbeiträge erhoben werden. Seit 2025 gelten bundeseinheitliche Werte (Ost = West). Für 2026 gilt in der Renten- und Arbeitslosenversicherung eine BBG von 8.450 €/Monat bzw. 101.400 €/Jahr, in der gesetzlichen Kranken- und Pflegeversicherung 5.812,50 €/Monat bzw. 69.750 €/Jahr (Sozialversicherungsrechengrößen-Verordnung 2026, BGBl. 2025 I Nr. 278).",
      "Auf Einkommen oberhalb der jeweiligen Grenze fallen keine zusätzlichen Beiträge mehr an – aber auch keine zusätzlichen Rentenpunkte. Wer mehr verdient, sollte die private Altersvorsorge entsprechend stärken.",
    ],
    related: ["gesetzliche-rente", "rentenpunkte", "nettolohn", "versicherungspflichtgrenze", "sozialversicherung", "krankenversicherungsbeitrag"],
  },
  {
    slug: "bruttolohn",
    term: "Bruttolohn",
    category: "Steuern",
    short:
      "Vertraglich vereinbartes Gehalt vor Abzug von Steuern und Sozialabgaben – je nach Steuerklasse bleiben 55–70 % netto.",
    paragraphs: [
      "Der Bruttolohn ist Dein vertraglich vereinbartes Gehalt vor Abzug von Steuern und Sozialabgaben. Er ist die Bemessungsgrundlage für Lohnsteuer, Soli, Kirchensteuer und Sozialversicherungsbeiträge.",
      "Je nach Steuerklasse und Einkommen bleiben rund 55–70 % als Netto übrig. Über Entgeltumwandlung, VL und steuerfreie Sachbezüge lässt sich der effektive Nettolohn erhöhen.",
    ],
    related: ["nettolohn", "lohnsteuer", "entgeltumwandlung"],
  },
  {
    slug: "cost-average-effekt",
    term: "Cost-Average-Effekt",
    category: "Vermögensaufbau",
    short:
      "Vorteil regelmäßiger Investitionen: bei niedrigen Kursen mehr Anteile, bei hohen weniger – der Durchschnittspreis sinkt.",
    paragraphs: [
      "Der Cost-Average-Effekt (Durchschnittskosteneffekt) beschreibt den Vorteil regelmäßiger, gleich hoher Investitionen. Bei niedrigen Kursen werden automatisch mehr Anteile gekauft, bei hohen Kursen weniger.",
      "Über lange Zeiträume glättet das Marktschwankungen und nimmt Anlegern die Sorge vor dem perfekten Einstiegszeitpunkt. Statistisch schlägt eine Einmalanlage den Sparplan zwar im Schnitt – psychologisch und für laufendes Einkommen ist der Sparplan oft die bessere Wahl.",
    ],
    related: ["sparplan", "etf", "anlagehorizont", "einmalanlage", "opportunitaetskosten"],
  },
  {
    slug: "festgeld",
    term: "Festgeld",
    category: "Vermögensaufbau",
    short:
      "Geldanlage mit festem Zinssatz und fester Laufzeit – nicht verfügbar während der Laufzeit, ideal für mittelfristiges Parken.",
    paragraphs: [
      "Festgeld ist eine Geldanlage bei einer Bank mit festem Zinssatz und fester Laufzeit (z. B. 6, 12 oder 36 Monate). Während der Laufzeit ist das Kapital nicht verfügbar.",
      "Festgeld eignet sich gut für mittelfristiges Parken von Geld, das Du sicher anlegen, aber nicht sofort brauchst. Für langfristigen Vermögensaufbau sind ETFs renditestärker.",
    ],
    related: ["tagesgeld", "etf", "inflation"],
  },
  {
    slug: "fondsgebundene-rentenversicherung",
    term: "Fondsgebundene Rentenversicherung",
    category: "Altersvorsorge",
    short:
      "Rentenversicherung mit Anlage in Investmentfonds – höhere Renditechancen, aber auch höheres Risiko als klassische Verträge.",
    paragraphs: [
      "Eine fondsgebundene Rentenversicherung kombiniert eine private Rentenversicherung mit der Anlage in Investmentfonds oder ETFs. Sie bietet höhere Renditechancen als klassische Verträge mit Garantiezins.",
      "Der Versicherungsmantel ermöglicht steuerliche Vorteile: Nach dem 62. Lebensjahr und 12 Jahren Laufzeit greift das Halbeinkünfteverfahren. Entscheidend sind niedrige Kosten und eine flexible Fondsauswahl.",
    ],
    related: ["fondspolice", "halbeinkuenfteverfahren", "altersvorsorge", "deckungskapital", "privatrente", "ueberschussbeteiligung"],
  },
  {
    slug: "gkv",
    term: "Gesetzliche Krankenversicherung (GKV)",
    category: "Versicherung",
    short:
      "Pflichtversicherung für Arbeitnehmer bis zur Versicherungspflichtgrenze – Beitrag 14,6 % vom Brutto plus Zusatzbeitrag.",
    paragraphs: [
      "Die gesetzliche Krankenversicherung (GKV) ist die Pflichtversicherung für Arbeitnehmer bis zur Versicherungspflichtgrenze (2026: 77.400 € brutto/Jahr, 6.450 €/Monat). Der allgemeine Beitragssatz beträgt 14,6 % des Bruttogehalts – jeweils zur Hälfte von Dir und Deinem Arbeitgeber getragen.",
      "Hinzu kommt ein kassenindividueller Zusatzbeitrag. Vorteile der GKV sind die solidarische Beitragsberechnung und die kostenlose Familienversicherung – ein Wechsel in die PKV will gut überlegt sein.",
    ],
    related: ["private-krankenversicherung", "zusatzbeitrag-gkv", "versicherungspflichtgrenze", "krankenversicherungsbeitrag"],
  },
  {
    slug: "haftpflichtversicherung",
    term: "Haftpflichtversicherung",
    category: "Versicherung",
    short:
      "Übernimmt Schäden, die Du anderen unbeabsichtigt zufügst – die wichtigste Versicherung überhaupt.",
    paragraphs: [
      "Die Privathaftpflichtversicherung übernimmt Schäden, die Du anderen Personen unbeabsichtigt zufügst – sei es ein verschütteter Kaffee auf dem Laptop oder ein Personenschaden mit lebenslangen Folgekosten.",
      "Sie ist die wichtigste Versicherung überhaupt, denn ohne sie haftest Du nach deutschem Recht unbegrenzt mit Deinem gesamten Vermögen. Empfohlene Deckungssumme: mindestens 10 Mio. € pauschal für Personen- und Sachschäden.",
    ],
    related: ["unfallversicherung", "risikolebensversicherung"],
  },
  {
    slug: "homeoffice-pauschale",
    term: "Homeoffice-Pauschale",
    category: "Steuern",
    short:
      "6 € pro Homeoffice-Arbeitstag steuerlich absetzbar – maximal 1.260 € pro Jahr (210 Tage, Stand 2026).",
    paragraphs: [
      "Mit der Homeoffice-Pauschale kannst Du als Arbeitnehmer 6 € pro Tag, an dem Du überwiegend zu Hause arbeitest, steuerlich absetzen – maximal 1.260 € pro Jahr (210 Tage, Stand 2026).",
      "Sie gehört zu den Werbungskosten und wird in Anlage N der Steuererklärung angegeben. Ein separater Nachweis ist nicht erforderlich – eine einfache Aufstellung der Homeoffice-Tage reicht aus.",
    ],
    related: ["werbungskosten", "pendlerpauschale", "lohnsteuererklaerung"],
  },
  {
    slug: "immobilienkredit",
    term: "Immobilienkredit",
    category: "Vermögensaufbau",
    short:
      "Zweckgebundenes Darlehen zur Finanzierung von Wohneigentum – die Immobilie selbst dient als Sicherheit.",
    paragraphs: [
      "Ein Immobilienkredit ist ein zweckgebundenes Darlehen zur Finanzierung von Wohneigentum. Die Immobilie selbst dient der Bank als Sicherheit, weshalb die Zinsen deutlich niedriger sind als bei Konsumkrediten.",
      "Der Zinssatz wird über eine sogenannte Sollzinsbindung für 5, 10, 15 oder 20 Jahre festgelegt. Üblich sind 10–20 % Eigenkapital sowie zusätzlich die Kaufnebenkosten (Grunderwerbsteuer, Notar, ggf. Makler).",
    ],
    related: ["sachwerte", "vermoegenswirksame-leistungen", "eigenkapital", "gesamtschuldnerschaft"],
  },
  {
    slug: "jaehrliche-renteninformation",
    term: "Jährliche Renteninformation",
    category: "Altersvorsorge",
    short:
      "Brief der Deutschen Rentenversicherung mit Deiner voraussichtlichen Rentenhöhe – ab dem 27. Lebensjahr automatisch.",
    paragraphs: [
      "Die jährliche Renteninformation ist ein Brief der Deutschen Rentenversicherung, der zeigt, wie viel Rente Du voraussichtlich bekommen wirst. Sie wird ab dem 27. Lebensjahr automatisch an alle Versicherten mit mindestens 5 Beitragsjahren verschickt.",
      "Wichtig sind drei Werte: die bisher erreichte Rente, die hochgerechnete Rente bei gleichbleibendem Einkommen und der reale Wert nach Inflation. Letzterer macht in der Regel die echte Rentenlücke deutlich.",
    ],
    related: ["rentenbescheid", "rentenluecke", "altersrente", "rentenversicherungsnummer"],
  },
  {
    slug: "kaskoversicherung",
    term: "Kaskoversicherung",
    category: "Versicherung",
    short:
      "Schützt Dein eigenes Fahrzeug – Teilkasko bei Diebstahl und Elementarschäden, Vollkasko zusätzlich bei eigenem Verschulden.",
    paragraphs: [
      "Die Kaskoversicherung schützt Dein eigenes Fahrzeug. Die Teilkasko übernimmt Schäden durch Diebstahl, Brand, Hagel, Wildunfall und Glasbruch. Die Vollkasko leistet zusätzlich bei selbstverschuldeten Unfällen.",
      "Vollkasko lohnt sich vor allem bei Neu- und Jungwagen sowie bei laufender Finanzierung. Über einen Selbstbehalt von 150–500 € kannst Du den Beitrag deutlich senken.",
    ],
    related: ["haftpflichtversicherung", "unfallversicherung"],
  },
  {
    slug: "kinderzulage",
    term: "Kinderzulage",
    category: "Förderungen",
    short:
      "Staatlicher Zuschuss zur Riester-Rente: 300 € pro Jahr und Kind (ab 2008 geboren) bzw. 185 € (vor 2008).",
    paragraphs: [
      "Die Kinderzulage ist ein staatlicher Zuschuss zur Riester-Rente. Für jedes ab 2008 geborene kindergeldberechtigte Kind erhältst Du 300 € pro Jahr zusätzlich zur Grundzulage, für vor 2008 geborene Kinder sind es 185 €.",
      "Voraussetzung ist, dass Du den Mindesteigenbeitrag von 4 % des Vorjahres-Bruttoeinkommens (abzüglich Zulagen) einzahlst und einen Dauerzulagenantrag gestellt hast.",
    ],
    related: ["riester-rente", "foerderungen"],
  },
  {
    slug: "lohnsteuer",
    term: "Lohnsteuer",
    category: "Steuern",
    short:
      "Einkommensteuer auf Arbeitseinkommen – wird direkt vom Arbeitgeber einbehalten und ans Finanzamt abgeführt.",
    paragraphs: [
      "Die Lohnsteuer ist die Einkommensteuer auf Dein Arbeitseinkommen. Sie wird direkt vom Arbeitgeber einbehalten und ans Finanzamt abgeführt – Du musst Dich also nicht selbst um die monatliche Abführung kümmern.",
      "Die Höhe richtet sich nach Steuerklasse, Bruttogehalt und ggf. Freibeträgen. Über die Steuererklärung kannst Du zu viel gezahlte Lohnsteuer zurückholen.",
    ],
    related: ["lohnsteuererklaerung", "steuerklasse", "bruttolohn", "steuerabzug", "steuerprogression"],
  },
  {
    slug: "lohnsteuererklaerung",
    term: "Lohnsteuererklärung",
    category: "Steuern",
    short:
      "Einkommensteuererklärung für Arbeitnehmer – im Schnitt 1.095 € Rückerstattung (Destatis, Veranlagungszeitraum 2020).",
    paragraphs: [
      "Die Lohnsteuererklärung (korrekt: Einkommensteuererklärung) ermöglicht Arbeitnehmern, zu viel gezahlte Steuern zurückzufordern. Im Schnitt erhalten Arbeitnehmer rund 1.095 € Steuerrückerstattung pro Jahr (Destatis, Lohn- und Einkommensteuerstatistik, Veranlagungszeitraum 2020).",
      "Absetzbar sind Werbungskosten, Sonderausgaben, außergewöhnliche Belastungen und haushaltsnahe Dienstleistungen. Für viele Konstellationen ist die Abgabe Pflicht – z. B. bei Lohnersatzleistungen über 410 € oder Kombination der Steuerklassen 3/5.",
    ],
    related: ["werbungskosten", "sonderausgaben", "lohnsteuer"],
  },
  {
    slug: "notfallreserve",
    term: "Notfallreserve",
    category: "Vermögensaufbau",
    short:
      "Liquides Finanzpolster von 3 Nettomonatsgehältern auf einem Tagesgeldkonto – für unvorhergesehene Ausgaben.",
    paragraphs: [
      "Eine Notfallreserve ist ein liquides Finanzpolster, das Du für unvorhergesehene Ausgaben wie Autoreparatur, Waschmaschinendefekt oder kurzfristigen Jobverlust bereithältst.",
      "Faustregel: 3 Nettomonatsgehälter auf einem Tagesgeldkonto – Selbstständige eher 6 Monate. Erst wenn die Notfallreserve steht, lohnt es sich, ernsthaft in ETFs zu investieren.",
    ],
    related: ["tagesgeld", "sparplan", "liquiditaet", "finanzielle-freiheit"],
  },
  {
    slug: "pendlerpauschale",
    term: "Pendlerpauschale",
    category: "Steuern",
    short:
      "Seit 2026: 0,38 € pro Kilometer ab dem ersten Kilometer des einfachen Arbeitswegs – als Werbungskosten absetzbar.",
    paragraphs: [
      "Mit der Pendlerpauschale (Entfernungspauschale) setzt Du den Weg zur Arbeit steuerlich ab: Seit dem 1. Januar 2026 gelten einheitlich 0,38 € je Entfernungskilometer – auch für die ersten 20 Kilometer (Steueränderungsgesetz 2025, § 9 Abs. 1 Satz 3 Nr. 4 EStG). Bis 2025 waren es 0,30 € und erst ab dem 21. Kilometer 0,38 €.",
      "Sie gilt pro Arbeitstag und einfacher Wegstrecke – unabhängig vom Verkehrsmittel, also auch für Bus, Bahn, Fahrrad oder zu Fuß. Bei tatsächlich höheren ÖPNV-Kosten kannst Du diese alternativ ansetzen.",
    ],
    related: ["werbungskosten", "homeoffice-pauschale", "lohnsteuererklaerung", "jobticket", "energiesteuer"],
  },
  {
    slug: "portfolio",
    term: "Portfolio",
    category: "Investments",
    short:
      "Gesamtheit aller Finanzanlagen einer Person – Aktien, ETFs, Anleihen, Immobilien etc. Ausgewogen reduziert es das Risiko.",
    paragraphs: [
      "Ein Portfolio bezeichnet die Gesamtheit aller Finanzanlagen einer Person – also Aktien, ETFs, Anleihen, Tagesgeld, Immobilien oder Gold.",
      "Ein ausgewogenes Portfolio reduziert das Risiko durch Diversifikation. Für Einsteiger ist eine einfache Kombination aus breitem Welt-ETF und Tagesgeld oft schon optimal.",
    ],
    related: ["diversifikation", "etf", "risikoprofil", "rebalancing", "musterportfolio", "depotverwaltung"],
  },
  {
    slug: "rendite",
    term: "Rendite",
    category: "Investments",
    short:
      "Prozentualer Gewinn einer Geldanlage bezogen auf das eingesetzte Kapital. Ein MSCI-World-ETF erzielte historisch ca. 7–10 % p. a.",
    paragraphs: [
      "Die Rendite ist der prozentuale Gewinn einer Geldanlage bezogen auf das eingesetzte Kapital – ausgedrückt in Prozent pro Jahr (p. a.).",
      "Wichtig ist die Realrendite nach Inflation und Steuern. Ein MSCI-World-ETF erzielte historisch rund 7–10 % p. a. nominal – nach Inflation bleiben ca. 5–7 % reale Rendite.",
    ],
    related: ["etf", "msci-world", "inflation", "gesamtkostenquote", "cost-average-effekt", "nettorendite", "opportunitaetskosten"],
  },
  {
    slug: "rentenpunkte",
    term: "Rentenpunkte",
    category: "Altersvorsorge",
    short:
      "Währung der gesetzlichen Rentenversicherung – pro Jahr Durchschnittsverdienst gibt es einen Punkt, 2026 ca. 39,32 € wert.",
    paragraphs: [
      "Rentenpunkte (offiziell Entgeltpunkte) sind die Währung der gesetzlichen Rentenversicherung. Wer ein Jahr lang das Durchschnittseinkommen aller Versicherten verdient, erhält genau einen Punkt.",
      "Der aktuelle Rentenwert liegt 2026 bei rund 39,32 € pro Punkt und Monat. 35 Punkte ergeben damit eine Bruttorente von ca. 1.376 €.",
    ],
    related: ["gesetzliche-rente", "altersrente", "rentenbescheid", "wartezeit-rente"],
  },
  {
    slug: "solidaritaetszuschlag",
    term: "Solidaritätszuschlag",
    category: "Steuern",
    short:
      "Zusatzsteuer auf die Einkommensteuer – seit 2021 nur noch für Gutverdiener ab ca. 62.603 € zu versteuerndem Einkommen (Single, 2026).",
    paragraphs: [
      "Der Solidaritätszuschlag (kurz: Soli) ist eine Zusatzsteuer in Höhe von 5,5 % auf die Einkommen- bzw. Lohnsteuer.",
      "Seit 2021 zahlen ihn nur noch Gutverdiener mit einem zu versteuernden Einkommen über rund 62.603 € (Singles, 2026) bzw. dem doppelten Betrag bei Verheirateten. Auf Kapitalerträge fällt der Soli weiterhin an.",
    ],
    related: ["abgeltungssteuer", "lohnsteuer", "nettolohn", "kapitalertragsteuer"],
  },
  {
    slug: "steuerklasse",
    term: "Steuerklasse",
    category: "Steuern",
    short:
      "Bestimmt, wie viel Lohnsteuer monatlich einbehalten wird – sechs Klassen, abhängig von Familienstand und Einkommen.",
    paragraphs: [
      "Die Steuerklasse bestimmt, wie viel Lohnsteuer Dein Arbeitgeber monatlich einbehält. Es gibt sechs Klassen: 1 für Singles, 2 für Alleinerziehende, 3/5 oder 4/4 (mit oder ohne Faktor) für Ehepaare und 6 für Zweitjobs.",
      "Bei Ehepaaren mit stark unterschiedlichen Einkommen lohnt sich oft die Kombination 3/5, bei ähnlichen Einkommen 4/4. Ein Wechsel ist mehrmals im Jahr möglich.",
    ],
    related: ["lohnsteuer", "lohnsteuererklaerung", "nettolohn"],
  },
  {
    slug: "unfallversicherung",
    term: "Unfallversicherung",
    category: "Versicherung",
    short:
      "Zahlt bei bleibenden Beeinträchtigungen durch Unfälle – 24/7, weltweit, auch in der Freizeit.",
    paragraphs: [
      "Die private Unfallversicherung zahlt bei bleibenden körperlichen Beeinträchtigungen durch Unfälle – 24 Stunden am Tag, weltweit und auch in der Freizeit. Sie ergänzt die gesetzliche Unfallversicherung, die nur am Arbeitsplatz und auf dem Arbeitsweg greift.",
      "Wichtig: Sie deckt nur Unfälle, keine Krankheiten. Wer seine Arbeitskraft umfassend absichern will, kommt um eine Berufsunfähigkeitsversicherung nicht herum.",
    ],
    related: ["berufsunfaehigkeitsversicherung", "grundfaehigkeitsversicherung", "haftpflichtversicherung"],
  },
  {
    slug: "vorauszahlung-steuer",
    term: "Vorauszahlung (Steuer)",
    category: "Steuern",
    short:
      "Vierteljährliche Steuervorauszahlung für Selbstständige und Anleger mit Nebeneinkünften – berechnet anhand der Vorjahressteuer.",
    paragraphs: [
      "Wer Selbstständig ist oder nennenswerte Nebeneinkünfte (z. B. aus Vermietung oder Kapitalvermögen) hat, muss in der Regel vierteljährliche Steuervorauszahlungen leisten. Sie werden vom Finanzamt anhand der zuletzt festgesetzten Steuer berechnet.",
      "Die Vorauszahlungen sind jeweils zum 10. März, Juni, September und Dezember fällig. Bei stark schwankenden Einkünften kannst Du beim Finanzamt eine Anpassung beantragen und so Nachzahlungen vermeiden.",
    ],
    related: ["lohnsteuererklaerung", "lohnsteuer", "steuerstundung"],
  },
  {
    slug: "versicherungspflichtgrenze",
    term: "Versicherungspflichtgrenze",
    category: "Versicherung",
    short:
      "Einkommensgrenze, ab der Arbeitnehmer in die PKV wechseln dürfen – 2026 allgemein 77.400 €/Jahr (6.450 €/Monat).",
    paragraphs: [
      "Die Versicherungspflichtgrenze (auch Jahresarbeitsentgeltgrenze, JAEG) ist die Einkommensgrenze, ab der Arbeitnehmer aus der gesetzlichen in die private Krankenversicherung wechseln dürfen. Für 2026 liegt die allgemeine JAEG bei 77.400 €/Jahr bzw. 6.450 €/Monat (Sozialversicherungsrechengrößen-Verordnung 2026, BGBl. 2025 I Nr. 278).",
      "Zu unterscheiden ist die besondere JAEG (\"Bestand PKV\"): Wer am 31.12.2002 bereits privat krankenversichert war, für den gilt 2026 die niedrigere Grenze von 69.750 €/Jahr bzw. 5.812,50 €/Monat – identisch mit der Beitragsbemessungsgrenze der GKV. Diese Unterscheidung ist entscheidend für Bestands-PKV-Versicherte, die den Status nicht verlieren wollen.",
      "Wer die Grenze überschreitet, ist nicht automatisch zum Wechsel verpflichtet – ein Verbleib in der GKV ist weiterhin möglich. Die Entscheidung sollte langfristig durchdacht werden, denn ein Rückwechsel ist ab dem 55. Lebensjahr in der Regel ausgeschlossen.",
    ],
    related: ["private-krankenversicherung", "gkv", "zusatzbeitrag-gkv", "beitragsbemessungsgrenze", "sozialversicherung"],
  },
  {
    slug: "witwenrente",
    term: "Witwenrente",
    category: "Altersvorsorge",
    short:
      "Leistung der gesetzlichen Rentenversicherung an überlebende Ehepartner – große Witwenrente 55 % der Rente des Verstorbenen.",
    paragraphs: [
      "Die Witwen- bzw. Witwerrente ist eine Leistung der gesetzlichen Rentenversicherung an überlebende Ehepartner und eingetragene Lebenspartner.",
      "Die große Witwenrente beträgt 55 % der Rente des Verstorbenen und wird ab dem 47. Lebensjahr oder bei Erwerbsminderung gezahlt. Die kleine Witwenrente (25 %) wird nur 24 Monate lang gezahlt. Eigene Einkünfte oberhalb eines Freibetrags werden anteilig angerechnet.",
    ],
    related: ["gesetzliche-rente", "altersrente", "risikolebensversicherung", "hinterbliebenenversorgung"],
  },
  {
    slug: "zusatzbeitrag-gkv",
    term: "Zusatzbeitrag (GKV)",
    category: "Versicherung",
    short:
      "Kassenindividueller Aufschlag auf den GKV-Beitrag – 2026 zwischen 1,7 % und 3,8 %. Ein Wechsel spart oft mehrere hundert Euro pro Jahr.",
    paragraphs: [
      "Der Zusatzbeitrag ist ein kassenindividueller Aufschlag auf den allgemeinen GKV-Beitragssatz von 14,6 %. 2026 liegt er je nach Krankenkasse zwischen rund 1,7 % und 3,8 %.",
      "Er wird wie der Hauptbeitrag jeweils zur Hälfte von Dir und Deinem Arbeitgeber getragen. Wer eine günstigere Kasse mit gleichem Leistungsumfang wählt, spart oft mehrere hundert Euro pro Jahr.",
    ],
    related: ["gkv", "private-krankenversicherung", "versicherungspflichtgrenze", "krankenversicherungsbeitrag"],
  },
  {
    slug: "zwei-saeulen-modell",
    term: "Zwei-Säulen-Modell",
    category: "Altersvorsorge",
    short:
      "Kombination aus gesetzlicher Rente und privater bzw. betrieblicher Altersvorsorge – Standard zur Vermeidung von Altersarmut.",
    paragraphs: [
      "Das Zwei-Säulen-Modell beschreibt die Kombination aus gesetzlicher Rente und ergänzender privater bzw. betrieblicher Altersvorsorge. Klassisch wird auch vom Drei-Säulen-Modell gesprochen – mit gesetzlicher, betrieblicher und privater Vorsorge als drei eigenständigen Säulen.",
      "Da die gesetzliche Rente allein zunehmend nicht ausreicht, ist der Aufbau einer zweiten (und dritten) Säule entscheidend, um Altersarmut zu vermeiden und den gewohnten Lebensstandard zu sichern.",
    ],
    related: ["altersvorsorge", "gesetzliche-rente", "betriebliche-altersvorsorge", "rentenluecke"],
  },
  {
    slug: "anleihe",
    term: "Anleihe",
    category: "Investments",
    short:
      "Festverzinsliches Wertpapier, mit dem Du Staaten oder Unternehmen Geld leihst und dafür Zinsen erhältst – sicherer als Aktien, aber renditeschwächer.",
    paragraphs: [
      "Eine Anleihe ist ein festverzinsliches Wertpapier, bei dem ein Investor einem Staat oder Unternehmen Geld leiht und dafür regelmäßige Zinszahlungen (Kupons) sowie am Laufzeitende den Nennwert zurückerhält.",
      "Anleihen gelten als sicherer als Aktien, bieten aber meist geringere Renditen. Bonität des Emittenten, Laufzeit und Zinsniveau bestimmen Risiko und Ertrag. Im Portfolio dienen sie häufig zur Stabilisierung.",
    ],
    related: ["wertpapier", "zins", "rendite", "bonitaet", "kapitalmarkt", "zinswende"],
  },
  {
    slug: "arbeitgeberzuschuss",
    term: "Arbeitgeberzuschuss",
    category: "Altersvorsorge",
    short:
      "Zuzahlung des Arbeitgebers – z. B. 15 % Pflichtzuschuss zur bAV bei Entgeltumwandlung.",
    paragraphs: [
      "Der Arbeitgeberzuschuss ist eine freiwillige oder verpflichtende Zuzahlung des Arbeitgebers zu betrieblichen Leistungen. Bekanntestes Beispiel: der 15-%-Pflichtzuschuss zur betrieblichen Altersvorsorge bei Entgeltumwandlung.",
      "Seit 2019 (Neuverträge) bzw. 2022 (Altverträge) ist der Arbeitgeber verpflichtet, mindestens 15 % Zuschuss zu zahlen, soweit er Sozialabgaben spart. Viele Arbeitgeber zahlen freiwillig mehr.",
    ],
    related: ["direktversicherung"],
  },
  {
    slug: "aussteuerung",
    term: "Aussteuerung",
    category: "Versicherung",
    short:
      "Ende des Krankengeldbezugs nach 78 Wochen – ohne Absicherung droht der finanzielle Absturz.",
    paragraphs: [
      "Die Aussteuerung bezeichnet das Ende des Krankengeldbezugs nach 78 Wochen Krankheit innerhalb von drei Jahren. Danach zahlt die gesetzliche Krankenkasse kein Krankengeld mehr.",
      "Wer dann nicht arbeiten kann, ist auf Krankentagegeld, Berufsunfähigkeitsrente oder Erwerbsminderungsrente angewiesen. Ohne private Absicherung droht ein massiver Einkommensverlust.",
    ],
    related: ["krankengeld", "krankentagegeld", "berufsunfaehigkeitsversicherung", "erwerbsminderungsrente", "lohnfortzahlung"],
  },
  {
    slug: "benchmark",
    term: "Benchmark",
    category: "Investments",
    short:
      "Vergleichsindex für Fonds und ETFs – meist schlagen aktive Fonds langfristig ihre Benchmark nicht.",
    paragraphs: [
      "Eine Benchmark ist ein Vergleichsindex, an dem die Performance eines Fonds oder ETFs gemessen wird – zum Beispiel der MSCI World für globale Aktienfonds.",
      "Ein aktiv gemanagter Fonds, der seine Benchmark nach Kosten dauerhaft unterbietet, ist meist schlechter als ein günstiger ETF. Studien zeigen: Über 10 Jahre schaffen das die meisten aktiven Fonds nicht.",
    ],
    related: ["msci-world", "etf", "index", "rendite", "fondsmanager"],
  },
  {
    slug: "bonitaet",
    term: "Bonität",
    category: "Vermögensaufbau",
    short:
      "Kreditwürdigkeit einer Person oder eines Unternehmens – entscheidend für Kredite, Mietverträge und mehr.",
    paragraphs: [
      "Die Bonität beschreibt die Kreditwürdigkeit einer Person oder eines Unternehmens. Sie bestimmt, ob und zu welchen Konditionen ein Kredit vergeben wird.",
      "In Deutschland ist die Schufa die wichtigste Bonitätsauskunft. Pünktliche Zahlungen, wenige laufende Kredite und ein gepflegtes Girokonto verbessern Deinen Score.",
    ],
    related: ["anleihe", "eigenkapital", "gesamtschuldnerschaft"],
  },
  {
    slug: "basisrente",
    term: "Basisrente (Rürup-Rente)",
    category: "Altersvorsorge",
    short:
      "Staatlich geförderte Altersvorsorge mit steuerlich absetzbaren Beiträgen bis 30.826 € jährlich (Single, 2026) – ideal für Selbstständige und Gutverdiener.",
    paragraphs: [
      "Die Basisrente (auch Rürup-Rente) ist eine staatlich geförderte Altersvorsorge. Beiträge sind bis zur Höchstgrenze (2026: 30.826 € Single / 61.652 € Verheiratete) zu 100 % als Sonderausgaben absetzbar (§ 10 Abs. 1 Nr. 2 EStG).",
      "Sie ist nicht beleihbar, nicht vererbbar (ohne Zusatzbaustein) und nicht kapitalisierbar – die Auszahlung erfolgt ausschließlich als lebenslange Rente. Besonders attraktiv für Selbstständige und Gutverdiener.",
    ],
    related: ["ruerup-rente", "altersvorsorge", "sonderausgaben", "vorsorgeaufwendungen"],
  },
  {
    slug: "deckungskapital",
    term: "Deckungskapital",
    category: "Altersvorsorge",
    short:
      "Angespartes Kapital in einer Lebens- oder Rentenversicherung – Basis für den Rückkaufswert bei Kündigung.",
    paragraphs: [
      "Das Deckungskapital ist das angesparte Kapital innerhalb einer Lebens- oder Rentenversicherung, das zur Deckung künftiger Leistungen vorgehalten wird. Es bildet die Grundlage für den Rückkaufswert.",
      "Bei Kündigung bekommst Du den Rückkaufswert ausgezahlt – meist deutlich weniger als die eingezahlten Beiträge, da Abschluss- und Verwaltungskosten abgezogen werden. Eine Beitragsfreistellung ist oft die bessere Wahl.",
    ],
    related: ["lebensversicherung", "fondsgebundene-rentenversicherung", "privatrente", "ueberschussbeteiligung"],
  },
  {
    slug: "direktversicherung",
    term: "Direktversicherung",
    category: "Altersvorsorge",
    short:
      "Durchführungsweg der bAV, bei dem der Arbeitgeber eine Lebens- oder Rentenversicherung für Dich abschließt.",
    paragraphs: [
      "Die Direktversicherung ist einer von fünf Durchführungswegen der betrieblichen Altersvorsorge. Der Arbeitgeber schließt für den Arbeitnehmer eine Lebens- oder Rentenversicherung ab.",
      "Beiträge bis 8 % der Beitragsbemessungsgrenze sind steuerfrei, bis 4 % auch sozialversicherungsfrei. Bei Jobwechsel kann der Vertrag mitgenommen, beim neuen Arbeitgeber fortgeführt oder privat weitergezahlt werden.",
    ],
    related: ["betriebliche-altersvorsorge", "entgeltumwandlung", "arbeitgeberzuschuss"],
  },
  {
    slug: "eigenkapital",
    term: "Eigenkapital",
    category: "Vermögensaufbau",
    short:
      "Eigenes Vermögen für eine Investition – bei Immobilien sollten es mindestens 20 % des Kaufpreises sein.",
    paragraphs: [
      "Eigenkapital ist das eigene Vermögen, das Du in eine Investition einbringst. Beim Immobilienkauf sollten es mindestens 20 % des Kaufpreises plus die Kaufnebenkosten sein.",
      "Je höher Dein Eigenkapital, desto besser die Konditionen beim Kredit und desto geringer das Risiko. Vollfinanzierungen ohne Eigenkapital sind möglich, aber deutlich teurer.",
    ],
    related: ["immobilienkredit", "tilgung", "hypothek", "bonitaet"],
  },
  {
    slug: "einmalanlage",
    term: "Einmalanlage",
    category: "Investments",
    short:
      "Investition eines größeren Betrags auf einmal – historisch oft rentabler als ein Sparplan, aber mit höherem Timing-Risiko.",
    paragraphs: [
      "Bei einer Einmalanlage wird ein größerer Betrag auf einmal investiert, statt monatlich in Raten. Über lange Anlagehorizonte ist sie historisch oft rentabler als ein vergleichbarer Sparplan.",
      "Der Nachteil: Das Timing-Risiko ist höher. Wer kurz vor einem Crash investiert, sieht zunächst Verluste. Eine Kombination aus Einmalanlage und Sparplan kann sinnvoll sein.",
    ],
    related: ["sparplan", "cost-average-effekt", "anlagehorizont", "depot"],
  },
  {
    slug: "erwerbsminderungsrente",
    term: "Erwerbsminderungsrente",
    category: "Versicherung",
    short:
      "Leistung der gesetzlichen Rentenversicherung bei dauerhafter Erwerbsminderung – im Schnitt nur 1.041 € im Monat.",
    paragraphs: [
      "Die Erwerbsminderungsrente ist eine Leistung der gesetzlichen Rentenversicherung für Versicherte, die aus gesundheitlichen Gründen dauerhaft nicht mehr arbeiten können.",
      "Wer weniger als 3 Stunden täglich arbeiten kann, erhält die volle Erwerbsminderungsrente; bei 3 bis unter 6 Stunden gibt es die halbe (§ 43 SGB VI). Der durchschnittliche Zahlbetrag lag im Rentenzugang 2024 bei 1.041 € monatlich – eine private Absicherung der Arbeitskraft ist deshalb meist unverzichtbar.",
    ],
    related: ["berufsunfaehigkeitsversicherung", "gesetzliche-rentenversicherung", "rentenluecke", "aussteuerung", "zurechnungszeit"],
  },
  {
    slug: "zurechnungszeit",
    term: "Zurechnungszeit",
    category: "Altersvorsorge",
    short:
      "Fiktive Beitragszeit bei Erwerbsminderung – bei Rentenbeginn 2026 gerechnet bis 66 Jahre und 3 Monate.",
    paragraphs: [
      "Die Zurechnungszeit ist eine fiktive Versicherungszeit in der gesetzlichen Rentenversicherung (§ 59 SGB VI). Wer eine Erwerbsminderungsrente bezieht, wird so gestellt, als hätte er bis zum maßgeblichen Endalter mit seinem bisherigen Durchschnittsverdienst weitergearbeitet. Bei einem Rentenbeginn im Jahr 2026 endet die Zurechnungszeit mit 66 Jahren und 3 Monaten.",
      "Das Endalter wird seit 2019 stufenweise angehoben – geregelt in der Übergangsvorschrift § 253a SGB VI, nicht in § 59 SGB VI. Es steigt zunächst um einen Monat pro Rentenbeginnjahr (2025: 66 Jahre 2 Monate, 2026: 66 Jahre 3 Monate, 2027: 66 Jahre 4 Monate) und erreicht erst bei einem Rentenbeginn ab 2031 die vollen 67 Jahre.",
      "Ohne Zurechnungszeit würde eine Erwerbsminderung in jungen Jahren nur die bis dahin gesammelten Entgeltpunkte abbilden. Sie ist damit der wichtigste Hebel für die Höhe der Erwerbsminderungsrente – gleicht die Einkommenslücke zum letzten Netto aber nicht aus.",
    ],
    related: ["erwerbsminderungsrente", "entgeltpunkte", "gesetzliche-rentenversicherung", "rentenluecke"],
  },
  {
    slug: "hinzuverdienstgrenze",
    term: "Hinzuverdienstgrenze",
    category: "Altersvorsorge",
    short:
      "Jahresbetrag, den Rentner zusätzlich verdienen dürfen – 2026 bei voller Erwerbsminderungsrente 20.763,75 €.",
    paragraphs: [
      "Die Hinzuverdienstgrenze legt fest, wie viel neben einer Rente verdient werden darf, ohne dass die Rente gekürzt wird. Bei voller Erwerbsminderungsrente liegt sie 2026 bei 20.763,75 € im Jahr, bei teilweiser Erwerbsminderungsrente bei mindestens 41.527,50 €. Der Wert bei der teilweisen Erwerbsminderungsrente ist ausdrücklich eine Mindestgrenze: Je nach früherem Einkommen kann die individuell berechnete Grenze höher liegen.",
      "Wird die Grenze überschritten, werden 40 Prozent des übersteigenden Betrags auf die Rente angerechnet. Bei Altersrenten ab der Regelaltersgrenze gibt es seit 2023 keine Hinzuverdienstgrenze mehr.",
    ],
    related: ["erwerbsminderungsrente", "gesetzliche-rentenversicherung", "zurechnungszeit"],
  },

  {
    slug: "fire-methode",
    term: "FIRE-Methode",
    category: "Vermögensaufbau",
    short:
      "Financial Independence, Retire Early – früh finanziell unabhängig durch hohe Sparquote und renditestarke Geldanlage.",
    paragraphs: [
      "FIRE steht für „Financial Independence, Retire Early“ – ein Lebensentwurf, bei dem durch eine hohe Sparquote (oft 50 %+) und renditestarke Investments in ETFs frühzeitig finanzielle Unabhängigkeit erreicht wird.",
      "Die 4-%-Regel besagt, dass Du das 25-Fache Deiner Jahresausgaben ansparen musst, um daraus dauerhaft leben zu können. Beispiel: 30.000 € jährliche Ausgaben → 750.000 € FIRE-Ziel.",
    ],
    related: ["sparplan", "etf", "notfallreserve", "finanzielle-freiheit"],
  },
  {
    slug: "freibetrag",
    term: "Freibetrag",
    category: "Steuern",
    short:
      "Betrag, der vom zu versteuernden Einkommen abgezogen wird – z. B. der Grundfreibetrag von 12.348 € (2026).",
    paragraphs: [
      "Ein Freibetrag ist ein Betrag, der vom zu versteuernden Einkommen abgezogen wird. Bekanntestes Beispiel ist der Grundfreibetrag von 12.348 € (Single, 2026), bis zu dem keine Einkommensteuer anfällt.",
      "Im Unterschied zur Freigrenze: Wird ein Freibetrag überschritten, wird nur der überschießende Teil besteuert. Bei einer Freigrenze wird hingegen der gesamte Betrag steuerpflichtig.",
    ],
    related: ["grundfreibetrag"],
  },
  {
    slug: "gehaltsverhandlung",
    term: "Gehaltsverhandlung",
    category: "Vermögensaufbau",
    short:
      "Gespräch mit dem Arbeitgeber über mehr Gehalt – ab 2026 gestützt durch die EU-Entgelttransparenzrichtlinie.",
    paragraphs: [
      "Eine Gehaltsverhandlung ist das Gespräch mit dem Arbeitgeber über eine Erhöhung des Gehalts. Ab 2026 stärkt die EU-Entgelttransparenzrichtlinie Deine Rechte – etwa beim Auskunftsanspruch auf Vergleichsgehälter.",
      "Eine gute Vorbereitung umfasst Marktdaten, Leistungsnachweise und realistische Zielwerte. Üblich sind Erhöhungen zwischen 5 und 15 % – beim Jobwechsel oft mehr.",
    ],
    related: ["bruttolohn", "nettolohn", "gehaltszettel", "entgeltumwandlung"],
  },
  {
    slug: "gesetzliche-rentenversicherung",
    term: "Gesetzliche Rentenversicherung",
    category: "Altersvorsorge",
    short:
      "Staatliches Pflichtrentensystem – finanziert je 9,3 % Beitrag von Arbeitnehmer und Arbeitgeber (2026).",
    paragraphs: [
      "Die gesetzliche Rentenversicherung ist das staatliche Pflicht-Rentensystem in Deutschland. Finanziert wird sie durch Beiträge von Arbeitnehmern und Arbeitgebern – je 9,3 % vom Bruttolohn (2026).",
      "Sie funktioniert nach dem Umlageverfahren: Heutige Beitragszahler finanzieren die heutigen Renten. Die Versorgungslücke wird größer – private und betriebliche Vorsorge sind unverzichtbar.",
    ],
    related: ["rentenpunkte", "beitragsbemessungsgrenze", "umlageverfahren", "rentenluecke", "rentenversicherungsnummer", "wartezeit-rente"],
  },
  {
    slug: "grundfreibetrag",
    term: "Grundfreibetrag",
    category: "Steuern",
    short:
      "Steuerfreier Grundbetrag des Einkommens – 12.348 € (Single, 2026); darunter keine Einkommensteuer.",
    paragraphs: [
      "Der Grundfreibetrag ist der steuerfreie Grundbetrag des Einkommens. 2026 liegt er bei 12.348 € für Singles bzw. 24.696 € für Verheiratete (Zusammenveranlagung).",
      "Bis zu dieser Höhe fällt keine Einkommensteuer an. Wer darüber liegt, zahlt Steuern ab dem ersten Euro Mehrbetrag – mit steigendem Steuersatz nach dem Progressionsprinzip.",
    ],
    related: ["einkommensteuer", "freibetrag", "progressionsvorbehalt", "steuererklaerung", "steuerprogression"],
  },
  {
    slug: "index",
    term: "Index",
    category: "Investments",
    short:
      "Korb aus Wertpapieren, der die Entwicklung eines Marktes abbildet – z. B. DAX oder MSCI World.",
    paragraphs: [
      "Ein Index ist ein Korb aus Wertpapieren, der die Entwicklung eines Marktes oder Segments abbildet. Bekannte Beispiele: DAX (40 größte deutsche Unternehmen), MSCI World (rund 1.500 Aktien weltweit) oder S&P 500 (USA).",
      "Indizes werden meist nach Marktkapitalisierung gewichtet. Über ETFs kannst Du sehr günstig in einen kompletten Index investieren – die Basis jeder passiven Anlagestrategie.",
    ],
    related: ["benchmark", "marktkapitalisierung", "wkn"],
  },
  {
    slug: "jobwechsel-altersvorsorge",
    term: "Jobwechsel und Altersvorsorge",
    category: "Altersvorsorge",
    short:
      "Was bei Jobwechsel mit bAV und Riester passiert – Mitnahme, Beitragsfreistellung oder private Weiterführung.",
    paragraphs: [
      "Bei einem Jobwechsel stellt sich die Frage, was mit Deiner betrieblichen Altersvorsorge (bAV) passiert. Riester-Verträge sind grundsätzlich portabel und laufen unverändert weiter.",
      "bAV-Verträge kannst Du beim neuen Arbeitgeber fortführen, beitragsfrei stellen oder privat weiter besparen. Welche Option am besten passt, hängt von Vertragstyp, Konditionen und Konditionen des neuen Arbeitgebers ab.",
    ],
    related: ["betriebliche-altersvorsorge", "direktversicherung", "entgeltumwandlung", "pensionskasse"],
  },
  {
    slug: "kirchensteuer",
    term: "Kirchensteuer",
    category: "Steuern",
    short:
      "Zuschlag von 8–9 % auf die Einkommensteuer für Kirchenmitglieder – als Sonderausgabe absetzbar.",
    paragraphs: [
      "Die Kirchensteuer ist ein Zuschlag auf die Einkommensteuer von 8 % (Bayern, Baden-Württemberg) bzw. 9 % (übrige Bundesländer) für Mitglieder einer steuererhebenden Kirche.",
      "Sie wird vom Arbeitgeber direkt einbehalten und ist im selben Jahr als Sonderausgabe absetzbar. Auf Kapitalerträge fällt sie zusätzlich zur Abgeltungssteuer an.",
    ],
    related: ["lohnsteuer", "einkommensteuer", "sonderausgaben", "steuerklasse"],
  },
  {
    slug: "krankengeld",
    term: "Krankengeld",
    category: "Versicherung",
    short:
      "Leistung der GKV nach 6 Wochen Lohnfortzahlung – 70 % brutto, max. 90 % netto, höchstens 78 Wochen.",
    paragraphs: [
      "Das Krankengeld ist eine Leistung der gesetzlichen Krankenversicherung. Es beginnt nach 6 Wochen Lohnfortzahlung durch den Arbeitgeber und beträgt 70 % des Bruttogehalts, jedoch maximal 90 % des Nettogehalts.",
      "Krankengeld wird höchstens 78 Wochen innerhalb von 3 Jahren wegen derselben Krankheit gezahlt. Danach endet der Anspruch – Stichwort Aussteuerung.",
    ],
    related: ["krankentagegeld", "aussteuerung", "lohnfortzahlung"],
  },
  {
    slug: "liquiditaet",
    term: "Liquidität",
    category: "Investments",
    short:
      "Wie schnell ein Vermögenswert in Bargeld umgewandelt werden kann – Tagesgeld ist liquid, Immobilien sind illiquid.",
    paragraphs: [
      "Liquidität beschreibt, wie schnell ein Vermögenswert in Bargeld umgewandelt werden kann, ohne große Wertverluste hinzunehmen. Tagesgeld ist hoch liquid, Immobilien dagegen sehr illiquid.",
      "Eine gute Finanzplanung sorgt für ausreichende Liquidität (Notgroschen 3–6 Nettogehälter) und investiert nur überschüssige Mittel langfristig.",
    ],
    related: ["notfallreserve"],
  },
  {
    slug: "minijob",
    term: "Minijob",
    category: "Steuern",
    short:
      "Beschäftigung bis 602 € monatlich (2026) – sozialversicherungsfrei, aber mit eingeschränkten Ansprüchen.",
    paragraphs: [
      "Ein Minijob ist ein Beschäftigungsverhältnis mit maximal 602 € Monatsverdienst (2026). Die Grenze ist dynamisch an den gesetzlichen Mindestlohn gekoppelt (Mindestlohn × 130 ÷ 3 = 13,90 € × 130 ÷ 3 = 602 €). Er ist für Arbeitnehmer steuer- und sozialversicherungsfrei – der Arbeitgeber zahlt pauschale Abgaben.",
      "Minijobber sind in der gesetzlichen Rentenversicherung pflichtversichert (kann auf Antrag entfallen) und sammeln so Rentenpunkte. Andere Sozialleistungen wie Arbeitslosengeld oder Krankengeld gibt es nicht.",
    ],
    related: ["sozialversicherung", "rentenpunkte", "gesetzliche-rentenversicherung", "nebeneinkuenfte"],
  },
  {
    slug: "musterportfolio",
    term: "Musterportfolio",
    category: "Investments",
    short:
      "Beispiel-Zusammensetzung eines Anlageportfolios – Einsteiger sind mit Ein-ETF-Portfolios meist am besten beraten.",
    paragraphs: [
      "Ein Musterportfolio ist eine Beispiel-Zusammensetzung eines Anlageportfolios. Für Einsteiger empfiehlt sich oft ein Ein-ETF-Portfolio auf den MSCI World oder FTSE All-World – breit gestreut, günstig und einfach.",
      "Fortgeschrittene kombinieren mehrere ETFs (z. B. Industrieländer + Schwellenländer + Small Caps) oder ergänzen Anleihen für mehr Stabilität.",
    ],
    related: ["etf", "portfolio", "risikostreuung", "rebalancing"],
  },
  {
    slug: "nachlassplanung",
    term: "Nachlassplanung",
    category: "Vermögensaufbau",
    short:
      "Maßnahmen zur Übertragung von Vermögen im Todesfall – mit Testament, Schenkungen und Vorsorgevollmacht.",
    paragraphs: [
      "Die Nachlassplanung umfasst alle Maßnahmen zur geordneten Übertragung von Vermögen im Todesfall – darunter Testament, Erbvertrag, Schenkungen zu Lebzeiten und Vorsorgevollmacht.",
      "Mit cleverer Planung lassen sich Erbschaftssteuern legal reduzieren – etwa durch die Nutzung von Freibeträgen alle 10 Jahre per Schenkung. Auch ein Testament vermeidet Streit unter Erben.",
    ],
    related: ["erbschaftssteuer", "vollmacht", "hinterbliebenenversorgung", "risikolebensversicherung", "zugewinngemeinschaft"],
  },
  {
    slug: "nebeneinkuenfte",
    term: "Nebeneinkünfte",
    category: "Steuern",
    short:
      "Einkünfte neben dem Hauptberuf – bis 410 € jährlich steuerfrei, darüber steuerpflichtig.",
    paragraphs: [
      "Nebeneinkünfte sind Einkünfte neben dem Hauptberuf – etwa aus Vermietung, Kapitalanlagen oder selbstständiger Tätigkeit. Bis 410 € jährlich bleiben sie nach dem Härteausgleich steuerfrei.",
      "Übersteigen sie 410 €, sind sie voll steuerpflichtig und in der Steuererklärung anzugeben. Eine Steuererklärungspflicht entsteht oft bereits ab dieser Grenze.",
    ],
    related: ["minijob"],
  },
  {
    slug: "rentenalter",
    term: "Rentenalter",
    category: "Altersvorsorge",
    short:
      "Gesetzliches Rentenalter in Deutschland: 67 Jahre (Jahrgänge ab 1964). Frühere Rente bedeutet 0,3 % Abschlag pro Monat.",
    paragraphs: [
      "Das gesetzliche Rentenalter liegt in Deutschland bei 67 Jahren für Jahrgänge ab 1964. Frühere Jahrgänge gehen anteilig früher in Rente.",
      "Ein vorzeitiger Renteneintritt ist möglich, führt aber zu dauerhaften Abzügen von 0,3 % pro vorgezogenem Monat. Wer 45 Beitragsjahre hat, kann mit 65 ohne Abschläge in Rente.",
    ],
    related: ["gesetzliche-rente", "rente-mit-63", "wartezeit-rente", "altersrente"],
  },
  {
    slug: "rohstoffe",
    term: "Rohstoffe",
    category: "Investments",
    short:
      "Eigene Anlageklasse aus Gold, Öl, Agrarrohstoffen u. a. – oft Inflationsschutz, im Portfolio meist Beimischung.",
    paragraphs: [
      "Rohstoffe wie Gold, Silber, Öl oder Agrarrohstoffe sind eine eigenständige Anlageklasse. Sie dienen häufig als Inflationsschutz und Diversifikation.",
      "Im Privatanleger-Portfolio spielen sie meist eine untergeordnete Rolle (5–10 %). Gold kann als physischer Sachwert oder über ETC gekauft werden; breite Rohstoffinvestments laufen über Rohstoff-ETFs.",
    ],
    related: ["sachwerte", "diversifikation", "portfolio", "inflation"],
  },
  {
    slug: "schufa",
    term: "Schufa",
    category: "Vermögensaufbau",
    short:
      "Wichtigste Bonitätsauskunft in Deutschland – ein negativer Eintrag erschwert Kredite, Mietverträge und mehr.",
    paragraphs: [
      "Die Schufa (Schutzgemeinschaft für allgemeine Kreditsicherung) ist die wichtigste Bonitätsauskunft in Deutschland. Sie sammelt Daten zu Krediten, Konten, Verträgen und Zahlungsausfällen.",
      "Ein negativer Eintrag erschwert oder verhindert Kredite, Mietverträge und manchmal sogar Handy- oder Stromverträge. Einmal jährlich hast Du Anrecht auf eine kostenlose Datenkopie.",
    ],
    related: ["bonitaet", "dispositionskredit", "kreditkarte", "immobilienkredit"],
  },
  {
    slug: "sozialversicherung",
    term: "Sozialversicherung",
    category: "Versicherung",
    short:
      "Deutsches System der Pflichtversicherungen – Renten-, Kranken-, Pflege-, Arbeitslosen- und Unfallversicherung.",
    paragraphs: [
      "Die Sozialversicherung ist das deutsche System der Pflichtversicherungen. Sie umfasst fünf Zweige: Renten-, Kranken-, Pflege-, Arbeitslosen- und Unfallversicherung.",
      "Arbeitnehmer und Arbeitgeber teilen sich die Beiträge in der Regel je zur Hälfte (Ausnahme: Unfallversicherung trägt der Arbeitgeber allein; der Beitragszuschlag für Kinderlose in der Pflegeversicherung trägt ausschließlich der Arbeitnehmer). Der Gesamtbeitragssatz 2026 liegt bei 42,3 % vom Bruttolohn (mit Kind) bzw. 42,9 % (kinderlos): Rentenversicherung 18,6 %, Arbeitslosenversicherung 2,6 %, Krankenversicherung 14,6 % zzgl. durchschnittlichem Zusatzbeitrag 2,9 %, Pflegeversicherung 3,6 % (mit Kind) bzw. 4,2 % (kinderlos, inkl. 0,6 % Zuschlag).",
    ],
    related: ["beitragsbemessungsgrenze", "versicherungspflichtgrenze", "gesetzliche-rentenversicherung", "nettolohn", "krankenversicherungsbeitrag"],
  },
  {
    slug: "steuererklaerung",
    term: "Steuererklärung",
    category: "Steuern",
    short:
      "Jährliche Abrechnung mit dem Finanzamt – im Schnitt 1.095 € Rückerstattung für Arbeitnehmer (Destatis, Veranlagungszeitraum 2020).",
    paragraphs: [
      "Die Steuererklärung ist die jährliche Abrechnung mit dem Finanzamt. Arbeitnehmer können dabei zu viel gezahlte Lohnsteuer zurückbekommen – im Schnitt 1.095 € pro Jahr (Destatis, Lohn- und Einkommensteuerstatistik, Veranlagungszeitraum 2020).",
      "Für Pflichtveranlagte ist die Abgabe bis 31. Juli des Folgejahres vorgeschrieben (mit Steuerberater bis Ende Februar des übernächsten Jahres). Freiwillige Steuererklärungen können bis zu 4 Jahre rückwirkend abgegeben werden.",
    ],
    related: ["werbungskosten", "sonderausgaben", "lohnsteuererklaerung", "einkommensteuer"],
  },
  {
    slug: "tilgung",
    term: "Tilgung",
    category: "Vermögensaufbau",
    short:
      "Rückzahlung des Kreditbetrags – bei Immobilien empfohlen mindestens 2–3 % Anfangstilgung.",
    paragraphs: [
      "Die Tilgung ist die Rückzahlung des Kreditbetrags an die Bank. Beim Immobilienkredit werden mindestens 2–3 % Anfangstilgung empfohlen – bei niedrigen Zinsen besser mehr.",
      "Je höher die Tilgung, desto schneller ist der Kredit abbezahlt und desto weniger Zinsen zahlst Du insgesamt. Sondertilgungen ermöglichen extra Rückzahlungen außerhalb der monatlichen Rate.",
    ],
    related: ["eigenkapital", "konsolidierung"],
  },
  {
    slug: "urlaubsgeld",
    term: "Urlaubsgeld",
    category: "Steuern",
    short:
      "Freiwillige Sonderzahlung des Arbeitgebers – voll steuerpflichtig; steuerfreie Alternative: Erholungsbeihilfe.",
    paragraphs: [
      "Urlaubsgeld ist eine freiwillige Sonderzahlung des Arbeitgebers. Es ist voll steuer- und sozialabgabenpflichtig – netto bleibt meist deutlich weniger als brutto.",
      "Die steuerfreie Alternative ist die Erholungsbeihilfe: bis zu 156 € für den Arbeitnehmer, 104 € für den Ehepartner und 52 € pro Kind – also bis zu 364 € für eine Familie.",
    ],
    related: ["bruttolohn", "lohnsteuer", "erholungsbeihilfe", "sachbezug"],
  },
  {
    slug: "vermietung-versteuerung",
    term: "Vermietung und Versteuerung",
    category: "Steuern",
    short:
      "Mieteinnahmen sind steuerpflichtig – Zinsen, AfA und Renovierungen mindern die Steuerlast.",
    paragraphs: [
      "Mieteinnahmen sind als Einkünfte aus Vermietung und Verpachtung steuerpflichtig. Sie werden mit Deinem persönlichen Einkommensteuersatz versteuert.",
      "Abzugsfähig sind Werbungskosten wie Kreditzinsen, Abschreibung (AfA, meist 2 % p. a.), Verwaltungs- und Renovierungskosten. Daraus entstehen oft anfänglich Verluste, die die Gesamtsteuerlast senken.",
    ],
    related: ["afa", "mietrendite", "werbungskosten", "immobilie-kapitalanlage"],
  },
  {
    slug: "wechselkosten",
    term: "Wechselkosten",
    category: "Investments",
    short:
      "Kosten beim Wechsel von Finanzprodukten – z. B. Stornogebühren oder steuerliche Folgen.",
    paragraphs: [
      "Wechselkosten bei Finanzprodukten sind die Kosten, die beim Wechsel eines Produkts entstehen – etwa Stornogebühren bei Versicherungen, neue Abschlusskosten oder steuerliche Folgen bei Depotauflösung.",
      "Ein Wechsel lohnt sich oft trotzdem, wenn das neue Produkt deutlich günstiger oder besser ist. Vorher genau rechnen: Wie schnell amortisiert sich der Wechsel?",
    ],
    related: ["depot", "private-krankenversicherung", "gesamtkostenquote"],
  },
  {
    slug: "wertpapier",
    term: "Wertpapier",
    category: "Investments",
    short:
      "Handelbares Finanzinstrument wie Aktien, Anleihen oder ETFs – wird an Börsen gehandelt und im Depot verwahrt.",
    paragraphs: [
      "Ein Wertpapier ist ein handelbares Finanzinstrument. Dazu zählen Aktien, Anleihen, Fonds und ETFs sowie Derivate wie Optionen oder Zertifikate.",
      "Wertpapiere werden an Börsen gehandelt und in einem Depot verwahrt. Sie sind das wichtigste Werkzeug für langfristigen Vermögensaufbau.",
    ],
    related: ["depot", "anleihe", "wkn"],
  },
  {
    slug: "zins",
    term: "Zins",
    category: "Investments",
    short:
      "Vergütung für das Überlassen von Kapital – bei Sparkonten erhältst Du Zinsen, bei Krediten zahlst Du sie.",
    paragraphs: [
      "Der Zins ist die Vergütung für das Überlassen von Kapital. Sparer erhalten Zinsen für ihre Einlagen, Kreditnehmer zahlen Zinsen für geliehenes Geld.",
      "Der EZB-Leitzins beeinflusst maßgeblich die Tagesgeld-, Festgeld- und Kreditzinsen. Seit 2022 ist das Zinsniveau in Europa wieder spürbar gestiegen.",
    ],
    related: ["anleihe", "zinswende"],
  },
  {
    slug: "zwangsvollstreckung",
    term: "Zwangsvollstreckung",
    category: "Vermögensaufbau",
    short:
      "Gerichtliches Verfahren zur Durchsetzung von Geldforderungen – durch gute Planung und Absicherung vermeidbar.",
    paragraphs: [
      "Die Zwangsvollstreckung ist das gerichtliche Verfahren zur Durchsetzung von Geldforderungen. Sie kann Gehaltspfändung, Kontopfändung oder Sachpfändung umfassen.",
      "Durch gute Finanzplanung, ausreichende Liquidität und passende Versicherungen lässt sie sich praktisch immer vermeiden. Bei drohender Zahlungsunfähigkeit hilft frühzeitige Schuldnerberatung.",
    ],
    related: ["insolvenz", "schufa", "gesamtschuldnerschaft", "bonitaet"],
  },
  {
    slug: "abfindung",
    term: "Abfindung",
    category: "Steuern",
    short:
      "Einmalige Zahlung des Arbeitgebers bei Beendigung des Arbeitsverhältnisses – steuerpflichtig, aber per Fünftelregelung optimierbar.",
    paragraphs: [
      "Eine Abfindung ist eine einmalige Zahlung des Arbeitgebers bei Beendigung des Arbeitsverhältnisses – etwa bei betriebsbedingter Kündigung oder Aufhebungsvertrag. Sie ist voll lohnsteuerpflichtig, aber sozialversicherungsfrei.",
      "Über die Fünftelregelung lässt sich die Steuerlast deutlich senken: Die Abfindung wird rechnerisch auf fünf Jahre verteilt, der Mehrsteuer-Anteil verfünffacht. Eine Faustregel für die Höhe sind 0,5 Bruttomonatsgehälter pro Beschäftigungsjahr.",
    ],
    related: ["lohnsteuer", "steuerprogression", "steuerstundung", "einkommensteuer"],
  },
  {
    slug: "afa",
    term: "AfA (Abschreibung)",
    category: "Steuern",
    short:
      "Absetzung für Abnutzung – Vermieter schreiben Immobilien über 50 Jahre ab (2 % p. a., Neubau ab 2023: 3 %).",
    paragraphs: [
      "Die AfA (Absetzung für Abnutzung) ermöglicht Vermietern, die Anschaffungs- und Herstellungskosten einer Immobilie über die Nutzungsdauer steuerlich abzuschreiben. Bei vermieteten Wohnimmobilien sind das 2 % jährlich (Altbau) bzw. 3 % bei Neubauten ab Baujahr 2023.",
      "Die AfA mindert die zu versteuernden Mieteinkünfte und kann anfänglich zu steuerlichen Verlusten führen, die das Gesamteinkommen senken. Nur der Gebäudewert ist abschreibbar – nicht das Grundstück.",
    ],
    related: ["immobilie-kapitalanlage", "vermietung-versteuerung", "werbungskosten", "mietrendite"],
  },
  {
    slug: "altersarmut",
    term: "Altersarmut",
    category: "Altersvorsorge",
    short:
      "Leben im Alter unter der Armutsgrenze – 19,6 % der Menschen ab 65 Jahren in Deutschland gelten als armutsgefährdet (Destatis, Mikrozensus 2024).",
    paragraphs: [
      "Altersarmut bezeichnet das Leben im Alter unterhalb der Armutsgrenze (60 % des Medianeinkommens). In Deutschland waren 2024 rund 19,6 % der Menschen ab 65 Jahren armutsgefährdet (Destatis, Mikrozensus 2024, Sozialberichterstattung).",
      "Hauptursachen sind niedrige gesetzliche Renten, unterbrochene Erwerbsbiografien und fehlende private Vorsorge. Wer früh anfängt und konsequent investiert, kann Altersarmut sicher vermeiden.",
    ],
    related: ["rentenluecke", "grundrente", "altersvorsorge", "gesetzliche-rente"],
  },
  {
    slug: "bausparen",
    term: "Bausparen",
    category: "Vermögensaufbau",
    short:
      "Kombination aus Spar- und Darlehensvertrag – staatlich gefördert über die Wohnungsbauprämie.",
    paragraphs: [
      "Bausparen ist eine Kombination aus Spar- und Darlehensvertrag. Zunächst wird Eigenkapital angespart, anschließend gibt es ein zinsgünstiges Darlehen zu festen Konditionen.",
      "Staatlich gefördert wird Bausparen über die Wohnungsbauprämie und – bei Eigennutzung – über die Wohnriester-Förderung. In Niedrigzinsphasen rechnen sich Bausparverträge oft nur als Zinssicherung für die Zukunft.",
    ],
    related: ["wohnriester", "wohnungsbaupraemie", "hypothek", "eigenkapital"],
  },
  {
    slug: "berufsunfaehigkeitsrente",
    term: "Berufsunfähigkeitsrente",
    category: "Versicherung",
    short:
      "Monatliche Zahlung aus einer Berufsunfähigkeitsversicherung, wenn Du Deinen Beruf nicht mehr ausüben kannst – 60–80 % Deines Nettos sind empfohlen.",
    paragraphs: [
      "Die Berufsunfähigkeitsrente ist die monatliche Zahlung aus einer privaten Berufsunfähigkeitsversicherung. Sie greift, wenn Du Deinen zuletzt ausgeübten Beruf aus gesundheitlichen Gründen zu mindestens 50 % nicht mehr ausüben kannst.",
      "Empfohlen werden 60–80 % Deines aktuellen Nettoeinkommens als Berufsunfähigkeitsrente. Sie ist klar von der gesetzlichen Erwerbsminderungsrente abzugrenzen, die deutlich niedriger ausfällt und schwerer zu bekommen ist.",
    ],
    related: ["berufsunfaehigkeitsversicherung", "erwerbsminderungsrente", "grundfaehigkeitsversicherung", "arbeitgeberzuschuss"],
  },
  {
    slug: "dax",
    term: "DAX",
    category: "Investments",
    short:
      "Deutscher Aktienindex der 40 größten börsennotierten Unternehmen Deutschlands – für private Anleger zu wenig diversifiziert.",
    paragraphs: [
      "Der DAX (Deutscher Aktienindex) umfasst die 40 größten und liquidesten börsennotierten Unternehmen Deutschlands. Er ist der wichtigste deutsche Aktienindex und Stimmungsbarometer für die deutsche Wirtschaft.",
      "Für private Anleger ist der DAX als alleiniges Investment zu wenig diversifiziert – ein globaler ETF auf MSCI World oder FTSE All-World streut deutlich breiter und ist langfristig die bessere Wahl.",
    ],
    related: ["aktie", "index", "boerse", "marktkapitalisierung"],
  },
  {
    slug: "deflation",
    term: "Deflation",
    category: "Investments",
    short:
      "Allgemeiner Rückgang des Preisniveaus – Gegenteil von Inflation und gefährlich für die Wirtschaft.",
    paragraphs: [
      "Deflation bezeichnet den allgemeinen Rückgang des Preisniveaus über einen längeren Zeitraum – das Gegenteil von Inflation. Sie klingt zunächst gut, ist aber wirtschaftlich gefährlich.",
      "Bei Deflation halten Konsumenten und Unternehmen Käufe und Investitionen zurück, weil sie auf sinkende Preise warten. Das führt zu Produktionsrückgang, Arbeitslosigkeit und einer Abwärtsspirale.",
    ],
    related: ["inflation", "inflationsrate", "leitzins", "kaufkraft"],
  },
  {
    slug: "dispositionskredit",
    term: "Dispositionskredit",
    category: "Vermögensaufbau",
    short:
      "Überziehungskredit auf dem Girokonto – mit 10–15 % Zinsen einer der teuersten Kredite überhaupt.",
    paragraphs: [
      "Der Dispositionskredit („Dispo“) ist ein vorab eingeräumter Überziehungskredit auf dem Girokonto. Du kannst Dein Konto bis zu einem festgelegten Rahmen überziehen, ohne einen separaten Kredit zu beantragen.",
      "Mit Zinsen zwischen 10 und 15 % ist er einer der teuersten Kredite überhaupt. Wer dauerhaft im Dispo ist, sollte ihn schnellstmöglich durch einen günstigeren Ratenkredit ablösen.",
    ],
    related: ["zins", "bonitaet", "schufa", "notfallreserve"],
  },
  {
    slug: "erbschaftssteuer",
    term: "Erbschaftssteuer",
    category: "Steuern",
    short:
      "Steuer beim Übergang von Vermögen durch Erbschaft oder Schenkung – Kinder haben 400.000 € Freibetrag alle 10 Jahre.",
    paragraphs: [
      "Die Erbschaftssteuer fällt beim Übergang von Vermögen durch Erbschaft oder Schenkung an. Die Höhe hängt vom Verwandtschaftsgrad und vom Wert des Vermögens ab.",
      "Kinder haben einen Freibetrag von 400.000 € pro Elternteil alle 10 Jahre, Ehepartner sogar 500.000 €. Größere Vermögen lassen sich durch gestaffelte Schenkungen legal weitgehend steuerfrei übertragen.",
    ],
    related: ["nachlassplanung", "freibetrag", "vollmacht", "hinterbliebenenversorgung", "vermoegensteuer", "zugewinngemeinschaft"],
  },
  {
    slug: "erholungsbeihilfe",
    term: "Erholungsbeihilfe",
    category: "Steuern",
    short:
      "Steuerfreie Sonderzahlung des Arbeitgebers für Erholung – bis 364 € für eine Familie.",
    paragraphs: [
      "Die Erholungsbeihilfe ist eine pauschal versteuerte (25 %) Sonderzahlung des Arbeitgebers für Erholungszwecke. Sie ist für den Arbeitnehmer steuer- und sozialabgabenfrei.",
      "Steuerfrei für Dich sind bis zu 156 € für Dich, 104 € für Deinen Partner und 52 € pro Kind pro Jahr – maximal 364 € für eine Familie. Die Auszahlung muss zeitnah zum Urlaub erfolgen.",
    ],
    related: ["urlaubsgeld", "sachbezug", "bruttolohn", "lohnsteuer"],
  },
  {
    slug: "finanzplan",
    term: "Finanzplan",
    category: "Vermögensaufbau",
    short:
      "Strukturierte Übersicht über Einnahmen, Ausgaben, Vermögen und Ziele – Grundlage jeder Finanzberatung.",
    paragraphs: [
      "Ein Finanzplan ist eine strukturierte Übersicht über Deine Einnahmen, Ausgaben, Vermögen, Schulden und finanziellen Ziele. Er ist die Grundlage jeder professionellen Finanzberatung.",
      "Ein guter Finanzplan zeigt, wo Du heute stehst, welche Ziele erreichbar sind und welche Bausteine (Vorsorge, Investments, Absicherung) Du dafür brauchst. Er wird mindestens jährlich überprüft und angepasst.",
    ],
    related: ["budgetplan", "haushaltsplan", "notfallreserve", "cashflow", "transparente-finanzberatung"],
  },
  {
    slug: "fondssparplan",
    term: "Fondssparplan",
    category: "Investments",
    short:
      "Regelmäßiger, automatischer Kauf von Fondsanteilen – nutzt den Cost-Average-Effekt für langfristigen Vermögensaufbau.",
    paragraphs: [
      "Ein Fondssparplan ist ein regelmäßiger, automatisierter Kauf von Fonds- oder ETF-Anteilen – meist monatlich ab 25 € möglich. Er ist ideal für Einsteiger und langfristigen Vermögensaufbau.",
      "Durch die monatlichen Käufe wirkt der Cost-Average-Effekt: Bei niedrigen Kursen bekommst Du mehr Anteile, bei hohen weniger – das glättet den durchschnittlichen Einstandskurs.",
    ],
    related: ["sparplan", "cost-average-effekt", "etf", "investmentfonds"],
  },
  {
    slug: "gehaltszettel",
    term: "Gehaltszettel",
    category: "Steuern",
    short:
      "Monatliche Aufstellung von Bruttolohn, Abzügen und Nettolohn – pflichtbestandteil des Arbeitsverhältnisses.",
    paragraphs: [
      "Der Gehaltszettel (Lohnabrechnung) ist die monatliche Aufstellung von Bruttolohn, Steuern, Sozialabgaben und Nettolohn. Er ist gesetzlich vorgeschrieben und muss verständlich sein.",
      "Wichtige Positionen sind Lohnsteuer, Solidaritätszuschlag, ggf. Kirchensteuer sowie Sozialversicherungsbeiträge (Renten-, Kranken-, Pflege- und Arbeitslosenversicherung). Vermögenswirksame Leistungen und Zuschüsse stehen separat.",
    ],
    related: ["bruttolohn", "nettolohn", "lohnsteuer", "sozialversicherung"],
  },
  {
    slug: "geldmarkt",
    term: "Geldmarkt",
    category: "Investments",
    short:
      "Markt für kurzfristige Geldanlagen mit Laufzeit unter einem Jahr – Tagesgeld, Festgeld und Geldmarkt-ETFs.",
    paragraphs: [
      "Der Geldmarkt ist der Markt für kurzfristige Geldanlagen mit Laufzeiten von unter einem Jahr. Dazu gehören Tagesgeld, kurzlaufende Festgelder und Geldmarktpapiere.",
      "Geldmarkt-ETFs sind eine moderne Alternative zu Tagesgeldkonten – sie investieren in besicherte Geldmarktinstrumente und folgen meist dem €STR-Zinssatz. Renditen sind ähnlich, aber Zugriff jederzeit über die Börse möglich.",
    ],
    related: ["tagesgeld", "festgeld", "leitzins", "liquiditaet"],
  },
  {
    slug: "gesetzliche-unfallversicherung",
    term: "Gesetzliche Unfallversicherung",
    category: "Versicherung",
    short:
      "Pflichtversicherung gegen Arbeitsunfälle und Berufskrankheiten – der Arbeitgeber zahlt die Beiträge allein.",
    paragraphs: [
      "Die gesetzliche Unfallversicherung ist eine Pflichtversicherung, die Arbeitnehmer bei Arbeitsunfällen, Wegeunfällen und Berufskrankheiten schützt. Träger sind die Berufsgenossenschaften.",
      "Die Beiträge zahlt allein der Arbeitgeber. Sie deckt jedoch nur Unfälle im beruflichen Kontext ab – Unfälle in Freizeit, Haushalt, Sport oder Urlaub sind nicht versichert. Eine private Unfallversicherung schließt diese Lücke.",
    ],
    related: ["unfallversicherung", "krankenversicherungsbeitrag", "sozialversicherung", "lohnfortzahlung"],
  },
  {
    slug: "haushaltsplan",
    term: "Haushaltsplan",
    category: "Vermögensaufbau",
    short:
      "Aufstellung aller monatlichen Einnahmen und Ausgaben – Grundlage für gesunde Finanzen und Sparquote.",
    paragraphs: [
      "Ein Haushaltsplan ist die systematische Aufstellung aller monatlichen Einnahmen und Ausgaben. Er zeigt, wie viel Geld am Ende des Monats übrig bleibt und wie viel Du investieren kannst.",
      "Empfohlen wird die 50-30-20-Regel: 50 % für Fixkosten, 30 % für Lebensgenuss, 20 % für Sparen und Investieren. Ein Haushaltsplan ist die Grundlage für jeden Vermögensaufbau.",
    ],
    related: ["budgetplan", "cashflow", "notfallreserve", "finanzplan", "konsolidierung"],
  },
  {
    slug: "immobilie-kapitalanlage",
    term: "Immobilie als Kapitalanlage",
    category: "Investments",
    short:
      "Vermietete Immobilie zur Renditeerzielung – Risiken: Leerstand, Mietausfall, steigende Zinsen.",
    paragraphs: [
      "Eine Immobilie als Kapitalanlage wird nicht selbst genutzt, sondern vermietet. Ziel sind Mieteinnahmen und langfristige Wertsteigerung. Steuerlich sind viele Kosten absetzbar.",
      "Risiken sind Leerstand, Mietausfall, Reparaturen und steigende Zinsen bei Anschlussfinanzierungen. Wer ohne große Eigenleistung gleiche Rendite mit weniger Aufwand will, fährt mit einem ETF-Portfolio oft besser.",
    ],
    related: ["mietrendite", "afa", "vermietung-versteuerung", "immobilienkredit"],
  },
  {
    slug: "insolvenz",
    term: "Insolvenz",
    category: "Vermögensaufbau",
    short:
      "Zahlungsunfähigkeit – Privatinsolvenz führt nach 3 Jahren Wohlverhaltensphase zur Restschuldbefreiung.",
    paragraphs: [
      "Insolvenz bedeutet, dass eine Person oder ein Unternehmen die fälligen Schulden nicht mehr begleichen kann. Privatpersonen können dann Privatinsolvenz beantragen.",
      "Nach einem außergerichtlichen Einigungsversuch folgt das gerichtliche Verfahren mit 3-jähriger Wohlverhaltensphase. Danach werden Restschulden erlassen – ein Neustart wird möglich.",
    ],
    related: ["schufa", "zwangsvollstreckung", "gesamtschuldnerschaft", "dispositionskredit"],
  },
  {
    slug: "jahresabschluss-persoenlich",
    term: "Jahresabschluss (persönlich)",
    category: "Vermögensaufbau",
    short:
      "Jährliche Überprüfung Deiner Finanzen – Vermögen, Schulden, Versicherungen, Vorsorge.",
    paragraphs: [
      "Ein persönlicher Jahresabschluss ist die jährliche Überprüfung Deiner Finanzen. Du analysierst Vermögen, Schulden, Versicherungen und Altersvorsorge und vergleichst die Entwicklung mit dem Vorjahr.",
      "Er hilft Dir zu erkennen, ob Du auf Kurs bist – und wo nachjustiert werden muss. Ideal: einmal jährlich, zum Jahreswechsel oder zu Deinem persönlichen „Finanztag“.",
    ],
    related: ["finanzplan", "budgetplan", "cashflow", "notfallreserve"],
  },
  {
    slug: "kreditkarte",
    term: "Kreditkarte",
    category: "Vermögensaufbau",
    short:
      "Zahlungsmittel mit kurzfristigem Kredit – Vorsicht bei Revolving-Karten mit hohen Zinsen.",
    paragraphs: [
      "Eine Kreditkarte ist ein Zahlungsmittel, das kurzfristig Kredit gewährt. Anders als bei der Debitkarte wird der Betrag nicht sofort, sondern meist gesammelt am Monatsende abgebucht.",
      "Vorsicht bei sogenannten Revolving-Kreditkarten: Hier kann der ausstehende Betrag in Raten bezahlt werden – zu Zinsen von oft 15–20 %. Charge-Karten oder kostenfreie Kreditkarten sind meist die bessere Wahl.",
    ],
    related: ["bonitaet", "schufa", "dispositionskredit", "online-banking"],
  },
  {
    slug: "laufzeit",
    term: "Laufzeit",
    category: "Investments",
    short:
      "Zeitraum eines Vertrags – bei Anlagen bestimmt sie den Zinseszinseffekt, bei Krediten die Tilgungsdauer.",
    paragraphs: [
      "Die Laufzeit beschreibt den vereinbarten Zeitraum eines Vertrags. Bei Sparplänen ist es die geplante Anlagedauer, bei Krediten die Zurückzahlungsdauer, bei Festgeld die Bindungsfrist.",
      "Bei Anlagen gilt: Je länger die Laufzeit, desto stärker wirkt der Zinseszinseffekt. Vorzeitige Kündigungen sind oft mit Kosten verbunden – beim Festgeld z. B. mit Zinsabschlägen.",
    ],
    related: ["festgeld", "anleihe", "tilgung", "immobilienkredit"],
  },
  {
    slug: "leitzins",
    term: "Leitzins",
    category: "Investments",
    short:
      "Zinssatz der Zentralbank – beeinflusst Tagesgeld-, Hypotheken- und Kreditzinsen.",
    paragraphs: [
      "Der Leitzins ist der Zinssatz, zu dem sich Banken bei der Zentralbank Geld leihen können. Er ist das wichtigste geldpolitische Instrument von EZB und Federal Reserve.",
      "Steigt der Leitzins, ziehen Tagesgeld- und Festgeldzinsen meist nach, ebenso Hypothekenzinsen. Seit 2022 hat die EZB den Leitzins stark erhöht und damit das Zinsumfeld in Europa deutlich verändert.",
    ],
    related: ["zins", "zinswende", "inflation", "geldmarkt", "inflationsrate", "kapitalmarkt"],
  },
  {
    slug: "mietrendite",
    term: "Mietrendite",
    category: "Investments",
    short:
      "Prozentualer Ertrag einer vermieteten Immobilie zum Kaufpreis – brutto und netto unterschiedlich.",
    paragraphs: [
      "Die Mietrendite beschreibt den prozentualen Ertrag einer vermieteten Immobilie im Verhältnis zum Kaufpreis. Sie ist die wichtigste Kennzahl für Immobilieninvestments.",
      "Bruttomietrendite = Jahresnettokaltmiete ÷ Kaufpreis. Eine Bruttomietrendite unter 4 % gilt heute als wenig attraktiv. Die Nettomietrendite berücksichtigt zusätzlich Bewirtschaftungskosten und Kaufnebenkosten.",
    ],
    related: ["immobilie-kapitalanlage", "nettokaltmiete", "afa", "vermietung-versteuerung"],
  },
  {
    slug: "nettokaltmiete",
    term: "Nettokaltmiete",
    category: "Investments",
    short:
      "Grundmiete ohne Nebenkosten – Basis für Mietrenditeberechnung und Mietspiegel.",
    paragraphs: [
      "Die Nettokaltmiete ist die Grundmiete für die Nutzung der Wohnung – ohne Betriebskosten und Heizkosten. Sie ist die Basis für Mietspiegel, Mietrenditeberechnung und gesetzliche Mieterhöhungen.",
      "Mieterhöhungen sind nur unter bestimmten Bedingungen zulässig: Anpassung an die ortsübliche Vergleichsmiete, Modernisierungsumlage oder Indexmietverträge. Maximal 20 % in 3 Jahren (Kappungsgrenze).",
    ],
    related: ["mietrendite", "immobilie-kapitalanlage", "vermietung-versteuerung"],
  },
  {
    slug: "online-banking",
    term: "Online-Banking",
    category: "Vermögensaufbau",
    short:
      "Digitale Verwaltung von Bankgeschäften über Internet oder App – Direktbanken bieten oft bessere Konditionen.",
    paragraphs: [
      "Online-Banking ist die digitale Verwaltung Deiner Bankgeschäfte über Internet oder App. Du erledigst Überweisungen, Daueraufträge und Kontoauszüge bequem von zu Hause aus.",
      "Direktbanken (z. B. ING, DKB, Comdirect) bieten oft höhere Zinsen, niedrigere Gebühren und kostenfreie Girokonten – sie sparen sich teure Filialnetze.",
    ],
    related: ["depot", "tagesgeld", "bonitaet"],
  },
  {
    slug: "passives-einkommen",
    term: "Passives Einkommen",
    category: "Vermögensaufbau",
    short:
      "Einkommen ohne aktive Arbeit – Dividenden, Mieteinnahmen, Zinsen. Basis finanzieller Freiheit.",
    paragraphs: [
      "Passives Einkommen ist Geld, das ohne aktive Arbeit fließt – zum Beispiel Dividenden, Mieteinnahmen, Zinsen oder Lizenzgebühren. Es ist die Grundlage finanzieller Freiheit.",
      "Wer langfristig genug passives Einkommen aufbaut, um seine Lebenshaltungskosten zu decken, erreicht finanzielle Unabhängigkeit (siehe FIRE-Methode). Faustregel: 25 × jährliche Ausgaben als investiertes Kapital.",
    ],
    related: ["dividende", "ausschuettend", "mietrendite", "immobilie-kapitalanlage", "finanzielle-freiheit"],
  },
  {
    slug: "pensionskasse",
    term: "Pensionskasse",
    category: "Altersvorsorge",
    short:
      "Durchführungsweg der bAV – eigenständige Versorgungseinrichtung, branchen- oder firmenweit organisiert.",
    paragraphs: [
      "Eine Pensionskasse ist einer der fünf Durchführungswege der betrieblichen Altersvorsorge. Sie ist eine eigenständige Versorgungseinrichtung, oft branchenweit organisiert.",
      "Beiträge bis 8 % der Beitragsbemessungsgrenze sind steuerfrei, bis 4 % auch sozialversicherungsfrei. Bei Jobwechsel kann der Vertrag mitgenommen werden – sofern der neue Arbeitgeber dieselbe Kasse akzeptiert.",
    ],
    related: ["betriebliche-altersvorsorge", "direktversicherung", "entgeltumwandlung", "betriebsrente"],
  },
  {
    slug: "risikostreuung",
    term: "Risikostreuung",
    category: "Investments",
    short:
      "Verteilung des Kapitals auf viele Anlagen – minimiert das Verlustrisiko ohne Renditeverlust.",
    paragraphs: [
      "Risikostreuung (Diversifikation) bedeutet, das Kapital auf verschiedene Anlagen, Branchen und Regionen zu verteilen. So sinkt das Verlustrisiko, ohne dass die erwartete Rendite leidet.",
      "Ein weltweiter ETF auf MSCI World oder FTSE All-World streut automatisch über 1.500–3.000 Aktien weltweit – mehr Diversifikation brauchst Du als Privatanleger nicht.",
    ],
    related: ["diversifikation", "portfolio", "etf", "msci-world", "musterportfolio"],
  },
  {
    slug: "sachbezug",
    term: "Sachbezug",
    category: "Steuern",
    short:
      "Nicht-monetäre Leistung des Arbeitgebers – bis 50 € monatlich steuer- und abgabenfrei.",
    paragraphs: [
      "Ein Sachbezug ist eine nicht-monetäre Leistung des Arbeitgebers – etwa Tankgutscheine, Restaurantgutscheine, Jobtickets oder Sachgeschenke.",
      "Bis 50 € monatlich (Sachbezugsfreigrenze) bleiben sie steuer- und sozialabgabenfrei. Sie sind eine attraktive Möglichkeit, das Nettogehalt zu erhöhen – ohne dass Steuern und Abgaben anfallen.",
    ],
    related: ["jobticket", "erholungsbeihilfe", "bruttolohn", "entgeltumwandlung"],
  },
  {
    slug: "selbstbehalt",
    term: "Selbstbehalt",
    category: "Versicherung",
    short:
      "Eigenanteil im Schadensfall – höherer Selbstbehalt senkt die Versicherungsprämie.",
    paragraphs: [
      "Der Selbstbehalt (auch Franchise oder Selbstbeteiligung) ist der Betrag, den Du im Schadensfall selbst trägst, bevor die Versicherung zahlt. Üblich bei Kfz-, Hausrat-, Privathaftpflicht- oder PKV-Tarifen.",
      "Ein höherer Selbstbehalt senkt die Prämie spürbar – sinnvoll, wenn Du Kleinschäden selbst tragen kannst. Bei großen Risiken (Haftpflicht) sollte der Selbstbehalt eher klein bleiben.",
    ],
    related: ["private-krankenversicherung", "haftpflichtversicherung", "kaskoversicherung", "versicherungslucke"],
  },
  {
    slug: "sondertilgung",
    term: "Sondertilgung",
    category: "Vermögensaufbau",
    short:
      "Zusätzliche Kredittilgung außerhalb der regulären Rate – verkürzt Laufzeit, spart Zinsen.",
    paragraphs: [
      "Eine Sondertilgung ist eine zusätzliche Rückzahlung auf einen Kredit außerhalb der vereinbarten Monatsrate. Sie verkürzt die Laufzeit und spart viele tausend Euro an Zinsen.",
      "Viele Banken erlauben kostenfreie Sondertilgungen von 5–10 % der Darlehenssumme jährlich – das sollte vertraglich vereinbart sein. Besonders sinnvoll bei steigenden Zinsen oder Sondereinnahmen wie Boni und Abfindungen.",
    ],
    related: ["tilgung", "immobilienkredit", "hypothek", "laufzeit"],
  },
  {
    slug: "tarifvertrag",
    term: "Tarifvertrag",
    category: "Vermögensaufbau",
    short:
      "Vertrag zwischen Gewerkschaft und Arbeitgeberverband über Gehälter und Arbeitsbedingungen.",
    paragraphs: [
      "Ein Tarifvertrag ist ein Vertrag zwischen einer Gewerkschaft und einem Arbeitgeberverband (oder einzelnen Arbeitgeber) über Arbeitsbedingungen, Gehälter, Urlaub und Sonderleistungen.",
      "Er gilt für tarifgebundene Arbeitnehmer einer Branche und sichert Mindeststandards. Häufig bessere Konditionen als bei nicht tarifgebundenen Arbeitgebern – etwa beim Urlaub, Weihnachtsgeld und Gehalt.",
    ],
    related: ["mindestlohn", "bruttolohn", "urlaubsgeld", "vermoegenswirksame-leistungen"],
  },
  {
    slug: "umlageverfahren",
    term: "Umlageverfahren",
    category: "Altersvorsorge",
    short:
      "Finanzierungsprinzip der gesetzlichen Rente – heutige Beitragszahler finanzieren heutige Renten.",
    paragraphs: [
      "Das Umlageverfahren ist das Finanzierungsprinzip der gesetzlichen Rentenversicherung: Die Beiträge der heutigen Erwerbstätigen werden direkt zur Auszahlung an die heutigen Rentner verwendet.",
      "Durch den demografischen Wandel (immer weniger Beitragszahler, immer mehr Rentner) gerät das System unter Druck. Im Gegensatz dazu steht das Kapitaldeckungsverfahren, bei dem jeder ein eigenes Kapital aufbaut.",
    ],
    related: ["gesetzliche-rentenversicherung", "gesetzliche-rente", "rentenpunkte", "rentenluecke"],
  },
  {
    slug: "verlustverrechnung",
    term: "Verlustverrechnung",
    category: "Steuern",
    short:
      "Verluste aus Kapitalanlagen mit Gewinnen verrechnen – senkt die Abgeltungssteuer.",
    paragraphs: [
      "Die Verlustverrechnung ermöglicht es, Verluste aus Kapitalanlagen mit Gewinnen zu verrechnen, um Steuern zu sparen. Banken führen dafür einen Verlusttopf pro Depot.",
      "Aktienverluste dürfen nur mit Aktiengewinnen verrechnet werden („Aktien-Verlusttopf“); sonstige Verluste sind breiter verrechenbar. Nicht verrechnete Verluste werden in Folgejahre vorgetragen.",
    ],
    related: ["abgeltungssteuer", "sparerpauschbetrag", "kapitalertragsteuer", "freistellungsauftrag"],
  },
  {
    slug: "vollmacht",
    term: "Vorsorgevollmacht",
    category: "Versicherung",
    short:
      "Ermächtigt eine Vertrauensperson, für Dich zu entscheiden, wenn Du es nicht mehr kannst.",
    paragraphs: [
      "Eine Vorsorgevollmacht ermächtigt eine Vertrauensperson, in Deinem Namen Entscheidungen zu treffen – medizinisch, finanziell und rechtlich –, wenn Du selbst nicht mehr handlungsfähig bist.",
      "Sie vermeidet, dass ein gerichtlicher Betreuer bestellt wird. Idealerweise kombiniert mit Patientenverfügung und Betreuungsverfügung. Notarielle Beglaubigung ist für Immobilien- und Bankgeschäfte oft notwendig.",
    ],
    related: ["nachlassplanung", "hinterbliebenenversorgung", "risikolebensversicherung", "zugewinngemeinschaft"],
  },
  {
    slug: "wertentwicklung",
    term: "Wertentwicklung",
    category: "Investments",
    short:
      "Performance eines Fonds oder ETFs über einen Zeitraum – Vergangenheitswerte sind keine Garantie.",
    paragraphs: [
      "Die Wertentwicklung (Performance) zeigt, wie sich ein Fonds, ETF oder Portfolio über einen bestimmten Zeitraum entwickelt hat. Sie wird in Prozent angegeben, meist 1, 3, 5 und 10 Jahre.",
      "Wichtig: Vergangenheitswerte sind kein Garant für zukünftige Renditen. Beachte zudem den Unterschied zwischen nominaler (vor Inflation) und realer Rendite (nach Inflation).",
    ],
    related: ["rendite", "benchmark", "msci-world", "cost-average-effekt", "nettorendite"],
  },
  {
    slug: "wohnriester",
    term: "Wohnriester",
    category: "Altersvorsorge",
    short:
      "Eigenheimrente – Nutzung des Riester-Guthabens für selbstgenutztes Wohneigentum.",
    paragraphs: [
      "Wohnriester (Eigenheimrente) ermöglicht die Nutzung des Riester-Guthabens für den Erwerb, Bau oder die Entschuldung von selbstgenutztem Wohneigentum.",
      "In der Ansparphase profitierst Du von Zulagen und Steuerersparnis. In der Rentenphase wird der Förderbetrag nachgelagert besteuert – über das fiktive Wohnförderkonto. Lohnt sich vor allem bei langer Restlaufzeit und hoher Förderquote.",
      "Wohnriester ist als Riester-Variante ebenfalls von der Reform betroffen: Mit dem Altersvorsorge-Reformgesetz (BGBl. I 2026 Nr. 156) endet zum 31. Dezember 2026 die Möglichkeit, neue Wohn-Riester-Verträge abzuschließen. Bestandsverträge laufen unverändert weiter. Das ab 2027 startende Altersvorsorgedepot ist als Rentenkapital konzipiert und ausdrücklich nicht für Immobilienentnahmen vorgesehen — wer die Immobilienförderung nutzen will, muss den Vertrag 2026 abschließen.",
    ],
    related: ["riester-rente", "bausparen", "wohnungsbaupraemie", "hypothek"],
  },
  {
    slug: "zulagenantrag",
    term: "Zulagenantrag",
    category: "Altersvorsorge",
    short:
      "Jährlicher Antrag auf Riester-Zulagen – meist automatisch durch den Anbieter gestellt.",
    paragraphs: [
      "Der Zulagenantrag ist der jährliche Antrag auf die staatlichen Riester-Zulagen (Grundzulage 175 €, Kinderzulage 300 € pro nach 2008 geborenem Kind).",
      "In der Regel stellt ihn Dein Anbieter automatisch (Dauerzulagenantrag) – Du musst ihn dafür einmal ermächtigen. Wird er nicht oder zu spät gestellt, gehen die Zulagen verloren (Frist: 2 Jahre nach Beitragsjahr).",
    ],
    related: ["riester-rente", "kinderzulage", "foerderungen", "altersvorsorgedepot"],
  },
  {
    slug: "auszahlungsplan",
    term: "Auszahlungsplan",
    category: "Altersvorsorge",
    short:
      "Strukturierte Entnahme aus Deinem Vermögen im Alter – z. B. monatlich aus einem ETF-Depot, ohne das Kapital zu schnell aufzubrauchen.",
    paragraphs: [
      "Ein Auszahlungsplan ist eine strukturierte Entnahme aus einem Vermögen im Alter – zum Beispiel monatlich 1.000 € aus einem ETF-Depot. Ziel ist es, das Kapital so zu entnehmen, dass es möglichst lange reicht.",
      "Die bekannteste Faustregel ist die 4-%-Regel: Wer jährlich 4 % seines Startvermögens entnimmt, kommt historisch über 30 Jahre meist nicht in die Verlustzone. In Niedrigzinsphasen werden teils 3,0–3,5 % empfohlen.",
    ],
    related: ["privatrente", "rentenluecke", "sparplan", "etf"],
  },
  {
    slug: "aktiv-vs-passiv",
    term: "Aktiv vs. Passiv",
    category: "Investments",
    short:
      "Aktive Fonds versuchen den Markt zu schlagen, passive ETFs bilden ihn nur ab – langfristig schaffen >80 % der aktiven Fonds das nicht.",
    paragraphs: [
      "Aktiv gemanagte Fonds werden von Fondsmanagern gesteuert, die versuchen, den Markt zu schlagen. Passive ETFs bilden lediglich einen Index wie den MSCI World ab.",
      "Studien zeigen: Über 10–15 Jahre schlagen mehr als 80 % der aktiv gemanagten Fonds ihre Benchmark nach Kosten nicht. Für Privatanleger sind günstige, breit gestreute ETFs deshalb in der Regel die bessere Wahl.",
    ],
    related: ["etf", "fondsmanager", "investmentfonds", "gesamtkostenquote"],
  },
  {
    slug: "betriebsrente",
    term: "Betriebsrente",
    category: "Altersvorsorge",
    short:
      "Auszahlung aus der betrieblichen Altersvorsorge im Rentenalter – nachgelagert besteuert und KV/PV-pflichtig.",
    paragraphs: [
      "Die Betriebsrente ist die Auszahlung aus der betrieblichen Altersvorsorge (bAV) im Rentenalter. Sie ergänzt die gesetzliche und die private Rente.",
      "In der Auszahlungsphase ist sie voll lohnsteuerpflichtig (nachgelagerte Besteuerung). Zudem fallen volle Beiträge zur gesetzlichen Kranken- und Pflegeversicherung an – das mindert den Nettobetrag spürbar.",
    ],
    related: ["betriebliche-altersvorsorge", "direktversicherung", "pensionskasse", "entgeltumwandlung"],
  },
  {
    slug: "boerse",
    term: "Börse",
    category: "Investments",
    short:
      "Organisierter Marktplatz für Wertpapiere – wichtigste deutsche Börsen sind Frankfurt (Xetra) und Stuttgart.",
    paragraphs: [
      "Die Börse ist ein organisierter Marktplatz für den Handel mit Wertpapieren wie Aktien, ETFs und Anleihen. Die wichtigsten deutschen Börsen sind Frankfurt (mit dem elektronischen Handelssystem Xetra) und Stuttgart.",
      "Privatanleger handeln nicht direkt an der Börse, sondern über einen Broker oder eine Bank, die die Orders weiterleitet. Außerbörslicher Handel (z. B. Tradegate, Gettex) ist oft günstiger.",
    ],
    related: ["aktie", "index", "dax", "msci-world", "kapitalmarkt"],
  },
  {
    slug: "budgetplan",
    term: "Budgetplan",
    category: "Vermögensaufbau",
    short:
      "Monatliche Aufstellung von Einnahmen und geplanten Ausgaben – die 50-30-20-Regel ist ein beliebter Rahmen.",
    paragraphs: [
      "Ein Budgetplan ist eine monatliche Aufstellung Deiner Einnahmen und geplanten Ausgaben. Er hilft, mehr zu sparen und gezielter zu investieren.",
      "Ein beliebter Rahmen ist die 50-30-20-Regel: 50 % Fixkosten, 30 % persönliche Bedürfnisse, 20 % Sparen und Investieren. Apps wie Finanzguru oder einfache Excel-Tabellen erleichtern den Überblick.",
    ],
    related: ["haushaltsplan", "cashflow", "notfallreserve", "finanzplan", "konsolidierung"],
  },
  {
    slug: "cashflow",
    term: "Cashflow",
    category: "Vermögensaufbau",
    short:
      "Monatlicher Geldfluss: Einnahmen minus Ausgaben – Voraussetzung für Vermögensaufbau und finanzielle Freiheit.",
    paragraphs: [
      "Der Cashflow beschreibt Deinen monatlichen Geldfluss – also Einnahmen minus Ausgaben. Ein positiver Cashflow ist die Grundvoraussetzung für Vermögensaufbau.",
      "Im Unterschied zum Gewinn berücksichtigt der Cashflow nur tatsächlich geflossene Zahlungen, keine Abschreibungen oder Buchgewinne. Wer dauerhaft mehr ausgibt als einnimmt, kann nicht investieren.",
    ],
    related: ["budgetplan", "haushaltsplan", "passives-einkommen", "notfallreserve", "finanzielle-freiheit", "konsolidierung"],
  },
  {
    slug: "depotverwaltung",
    term: "Depotverwaltung",
    category: "Investments",
    short:
      "Pflege Deines Wertpapierdepots – Rebalancing, Steueroptimierung und regelmäßige Strategieprüfung.",
    paragraphs: [
      "Die Depotverwaltung umfasst alle Maßnahmen zur Pflege eines Wertpapierdepots – Rebalancing, Steueroptimierung, Anpassung an Lebensphasen und regelmäßige Überprüfung der Anlagestrategie.",
      "Ein gut gepflegtes Depot ist klar strukturiert, kostengünstig und passt zu Deinem Anlagehorizont. Einmal jährlich solltest Du Allokation und Performance prüfen – mehr ist meist unnötig.",
    ],
    related: ["depot", "portfolio", "gesamtkostenquote"],
  },
  {
    slug: "durchschnittskosteneffekt",
    term: "Durchschnittskosteneffekt",
    category: "Investments",
    short:
      "Cost-Average-Effekt – durch regelmäßiges Investieren wird der durchschnittliche Einstandskurs geglättet.",
    paragraphs: [
      "Der Durchschnittskosteneffekt (Cost-Average-Effekt) entsteht durch regelmäßige, gleichbleibende Investitionen – etwa per ETF-Sparplan. Bei fallenden Kursen kaufst Du mehr Anteile, bei steigenden weniger.",
      "Der Effekt glättet den durchschnittlichen Einstandskurs und reduziert das Timing-Risiko. Bei langem Anlagehorizont schlägt eine Einmalanlage statistisch zwar oft den Sparplan – psychologisch ist der Cost-Average aber leichter durchzuhalten.",
    ],
    related: ["cost-average-effekt", "sparplan", "etf", "anlagehorizont"],
  },
  {
    slug: "einkommensteuer",
    term: "Einkommensteuer",
    category: "Steuern",
    short:
      "Steuer auf das Einkommen natürlicher Personen – progressiv von 0 % bis 45 % plus Soli.",
    paragraphs: [
      "Die Einkommensteuer ist die Steuer auf das Einkommen natürlicher Personen in Deutschland. Sie ist progressiv aufgebaut: Der Steuersatz steigt mit dem Einkommen von 0 % (bis Grundfreibetrag) bis zum Spitzensteuersatz von 45 %.",
      "Der Grundfreibetrag liegt 2026 bei 12.348 € (Single). Hinzu kommen Solidaritätszuschlag und ggf. Kirchensteuer. Werbungskosten, Sonderausgaben und Vorsorgeaufwendungen senken das zu versteuernde Einkommen.",
    ],
    related: ["grundfreibetrag", "steuerprogression", "vermoegensteuer", "energiesteuer"],
  },
  {
    slug: "elterngeld",
    term: "Elterngeld",
    category: "Steuern",
    short:
      "Staatliche Leistung nach Geburt – 65–67 % des Nettoeinkommens für bis zu 14 Monate, mit Progressionsvorbehalt.",
    paragraphs: [
      "Das Elterngeld ist eine staatliche Leistung nach der Geburt eines Kindes. Es ersetzt 65–67 % des wegfallenden Nettoeinkommens für bis zu 14 Monate (bei paritätischer Aufteilung der Eltern).",
      "Es unterliegt dem Progressionsvorbehalt: Selbst steuerfrei, erhöht es jedoch den Steuersatz auf das übrige Einkommen. Bei ElterngeldPlus kann der Bezug auf bis zu 24 Monate gestreckt werden – mit reduzierter Monatszahlung.",
    ],
    related: ["progressionsvorbehalt", "steuerklasse", "lohnsteuer", "lohnfortzahlung"],
  },
  {
    slug: "finanzielle-freiheit",
    term: "Finanzielle Freiheit",
    category: "Vermögensaufbau",
    short:
      "Passive Einnahmen decken die Lebenshaltungskosten – nach der 4-%-Regel das 25-Fache der Jahresausgaben als Kapital.",
    paragraphs: [
      "Finanzielle Freiheit bedeutet, dass passive Einnahmen Deine Lebenshaltungskosten dauerhaft decken – ohne dass Du aktiv arbeiten musst.",
      "Nach der 4-%-Regel brauchst Du dafür das 25-Fache Deiner jährlichen Ausgaben als investiertes Kapital. Beispiel: 36.000 € Jahresausgaben → 900.000 € Zielvermögen. Wie schnell Du dorthin kommst, hängt von Sparquote, Rendite und Lebensstil ab.",
    ],
    related: ["fire-methode", "passives-einkommen", "notfallreserve", "cashflow"],
  },
  {
    slug: "fondsmanager",
    term: "Fondsmanager",
    category: "Investments",
    short:
      "Verwaltet aktiv gemanagte Fonds – schlägt langfristig selten einen einfachen ETF.",
    paragraphs: [
      "Ein Fondsmanager verwaltet aktiv gemanagte Investmentfonds und entscheidet über Kauf und Verkauf der Wertpapiere im Fonds. Ziel ist es, den Markt (die Benchmark) zu schlagen.",
      "Studien zeigen: Langfristig schneiden Fondsmanager nach Kosten selten besser ab als ein einfacher Index-ETF. Die TER aktiver Fonds liegt meist bei 1,5–2 %, plus Ausgabeaufschläge – das frisst die Rendite.",
    ],
    related: ["investmentfonds", "aktiv-vs-passiv", "gesamtkostenquote", "benchmark"],
  },
  {
    slug: "garantiezins",
    term: "Garantiezins",
    category: "Versicherung",
    short:
      "Vom Versicherer garantierter Mindestzins bei klassischen Lebens-/Rentenversicherungen – aktuell nur 1,0 %.",
    paragraphs: [
      "Der Garantiezins (Höchstrechnungszins) ist der vom Versicherer garantierte Mindestzins bei klassischen Lebens- und Rentenversicherungen. Seit 2025 liegt er bei nur noch 1,0 % – historisch sehr niedrig.",
      "Klassische Garantieprodukte lohnen sich dadurch kaum noch. Fondsgebundene Rentenversicherungen oder ETF-Sparpläne bieten meist deutlich bessere Renditechancen.",
    ],
    related: ["lebensversicherung", "ueberschussbeteiligung", "privatrente", "riester-rente"],
  },
  {
    slug: "gesamtschuldnerschaft",
    term: "Gesamtschuldnerschaft",
    category: "Vermögensaufbau",
    short:
      "Mehrere Personen haften gemeinsam für eine Schuld – jeder kann für den Gesamtbetrag belangt werden.",
    paragraphs: [
      "Bei der Gesamtschuldnerschaft haften mehrere Personen gemeinsam für eine Schuld – etwa Ehepartner bei einem gemeinsamen Immobilienkredit. Jeder Schuldner kann vom Gläubiger für die gesamte Schuld in Anspruch genommen werden.",
      "Das ist wichtig zu wissen: Wer einen Kredit zu zweit aufnimmt, haftet nicht nur für seinen Anteil, sondern für die volle Summe. Im Innenverhältnis können die Schuldner einen Ausgleich vereinbaren.",
    ],
    related: ["hypothek", "immobilienkredit", "bonitaet"],
  },
  {
    slug: "hinterbliebenenversorgung",
    term: "Hinterbliebenenversorgung",
    category: "Versicherung",
    short:
      "Finanzielle Absicherung von Ehepartner und Kindern im Todesfall – gesetzliche Renten plus private Risikoabsicherung.",
    paragraphs: [
      "Die Hinterbliebenenversorgung sichert Ehepartner und Kinder finanziell ab, wenn der Hauptverdiener stirbt. Sie umfasst gesetzliche Leistungen (Witwen-, Witwer- und Waisenrente) sowie private Vorsorge.",
      "Die gesetzliche Witwenrente liegt meist nur bei 55 % der Rente des Verstorbenen – oft zu wenig. Eine günstige Risikolebensversicherung schließt diese Lücke zuverlässig.",
    ],
    related: ["witwenrente", "risikolebensversicherung", "betriebliche-altersvorsorge", "nachlassplanung"],
  },
  {
    slug: "hypothek",
    term: "Hypothek",
    category: "Vermögensaufbau",
    short:
      "Durch eine Immobilie besichertes Darlehen – klassische Form der Immobilienfinanzierung, heute meist als Annuitätendarlehen.",
    paragraphs: [
      "Eine Hypothek ist ein Darlehen, das durch eine Immobilie als Sicherheit abgesichert wird. Sie ist die klassische Form der Immobilienfinanzierung und wird heute meist als Annuitätendarlehen vergeben.",
      "In der Praxis nutzt man in Deutschland eher die Grundschuld als die klassische Hypothek – sie ist flexibler. Wichtige Kennzahlen sind Sollzins, Tilgung, Zinsbindung und Sondertilgungsoption.",
    ],
    related: ["eigenkapital", "gesamtschuldnerschaft"],
  },
  {
    slug: "inflationsrate",
    term: "Inflationsrate",
    category: "Investments",
    short:
      "Misst, wie stark Preise im Vergleich zum Vorjahr gestiegen sind – 2022/23 zeitweise über 8 % in Deutschland.",
    paragraphs: [
      "Die Inflationsrate misst, wie stark die Verbraucherpreise im Vergleich zum Vorjahresmonat gestiegen sind. Grundlage ist der Verbraucherpreisindex, der vom Statistischen Bundesamt veröffentlicht wird.",
      "In Deutschland lag sie 2022 und 2023 zeitweise über 8 % – ein Vielfaches des EZB-Zielwerts von 2 %. Wer Geld ausschließlich auf dem Girokonto hält, verliert in solchen Phasen massiv an Kaufkraft.",
    ],
    related: ["inflation", "kaufkraft", "leitzins", "sachwerte"],
  },
  {
    slug: "investmentfonds",
    term: "Investmentfonds",
    category: "Investments",
    short:
      "Bündelt Kapital vieler Anleger und investiert es in Wertpapiere – aktiv gemanagt oder als passiver ETF.",
    paragraphs: [
      "Ein Investmentfonds bündelt das Kapital vieler Anleger und investiert es in verschiedene Wertpapiere wie Aktien, Anleihen oder Immobilien. Man unterscheidet aktiv gemanagte Fonds und passive ETFs.",
      "Für Privatanleger sind ETFs in der Regel die bessere Wahl: günstiger, transparenter und langfristig meist renditestärker. Aktive Fonds lohnen sich nur in seltenen Nischen.",
    ],
    related: ["etf", "fondsmanager", "sondervermoegen", "gesamtkostenquote"],
  },
  {
    slug: "jobticket",
    term: "Jobticket",
    category: "Steuern",
    short:
      "Vom Arbeitgeber bezuschusstes oder bezahltes ÖPNV-Ticket – seit 2020 steuer- und sozialabgabenfrei.",
    paragraphs: [
      "Das Jobticket ist ein vom Arbeitgeber bezuschusstes oder voll bezahltes ÖPNV-Ticket für den Arbeitsweg. Seit 2020 ist es steuer- und sozialabgabenfrei, wenn es zusätzlich zum Lohn gewährt wird.",
      "Achtung: Die Steuerersparnis wird mit der Pendlerpauschale verrechnet – das Finanzamt zieht den steuerfreien Zuschuss von Deinen Werbungskosten ab.",
    ],
    related: ["sachbezug", "pendlerpauschale", "werbungskosten"],
  },
  {
    slug: "kapitalmarkt",
    term: "Kapitalmarkt",
    category: "Investments",
    short:
      "Markt für langfristige Kapitalanlagen wie Aktien, Anleihen und ETFs – Grundlage für privaten Vermögensaufbau.",
    paragraphs: [
      "Der Kapitalmarkt ist der Markt für langfristige Kapitalanlagen mit Laufzeiten von über einem Jahr. Hier werden Aktien, Anleihen und ETFs gehandelt.",
      "Im Gegensatz zum Geldmarkt geht es um langfristige Finanzierung von Staaten und Unternehmen. Für Privatanleger ist der Kapitalmarkt – meist über breit gestreute ETFs – die wichtigste Quelle für Renditen oberhalb der Inflation.",
    ],
    related: ["aktie", "anleihe", "boerse", "leitzins"],
  },
  {
    slug: "konsolidierung",
    term: "Konsolidierung",
    category: "Vermögensaufbau",
    short:
      "Mehrere Kredite oder Verträge zu einem zusammenführen – meist mit dem Ziel günstigerer Konditionen.",
    paragraphs: [
      "Konsolidierung im Finanzkontext bedeutet, mehrere Kredite oder Verträge zu einem zusammenzuführen – zum Beispiel mehrere teure Konsumkredite und Dispo durch einen günstigeren Ratenkredit ersetzen (Umschuldung).",
      "Das senkt Zinsen, vereinfacht die Verwaltung und gibt Dir wieder Übersicht. Voraussetzung sind gute Bonität und ein klarer Tilgungsplan – sonst wandert die Schuldenspirale nur weiter.",
    ],
    related: ["budgetplan", "haushaltsplan", "cashflow", "tilgung"],
  },
  {
    slug: "krankenversicherungsbeitrag",
    term: "Krankenversicherungsbeitrag",
    category: "Versicherung",
    short:
      "2026 in der GKV 14,6 % vom Brutto plus Zusatzbeitrag – je hälftig von Arbeitnehmer und Arbeitgeber.",
    paragraphs: [
      "Der Krankenversicherungsbeitrag in der GKV beträgt 2026 14,6 % des beitragspflichtigen Bruttogehalts (allgemeiner Beitrag) plus kassenindividueller Zusatzbeitrag (1,7–3,8 %).",
      "Arbeitnehmer und Arbeitgeber zahlen die Beiträge je zur Hälfte. Wer in einer teuren Kasse ist, kann durch Wechsel oft mehrere hundert Euro pro Jahr sparen.",
    ],
    related: ["gkv", "zusatzbeitrag-gkv", "beitragsbemessungsgrenze", "sozialversicherung"],
  },
  {
    slug: "langzeitpflege",
    term: "Langzeitpflege",
    category: "Versicherung",
    short:
      "Dauerhafte professionelle Pflege – Pflegeheime kosten 2026 im Schnitt 3.000–5.000 € monatlich Eigenanteil.",
    paragraphs: [
      "Langzeitpflege bezeichnet die dauerhafte professionelle Pflege bei eingeschränkter Selbstständigkeit. Sie kann ambulant zu Hause oder stationär im Pflegeheim erfolgen.",
      "Pflegeheime kosten 2026 durchschnittlich 3.000–5.000 € Eigenanteil monatlich – nach Abzug der Pflegekassenleistungen. Ohne private Pflegezusatzversicherung droht der Rückgriff aufs eigene Vermögen oder die Kinder.",
    ],
    related: ["pflegeversicherung", "pflegegrad", "pflegeluecke", "pflegemindestlohn"],
  },
  {
    slug: "lohnfortzahlung",
    term: "Lohnfortzahlung",
    category: "Versicherung",
    short:
      "Gesetzliche Pflicht des Arbeitgebers: Bei Krankheit zahlt er bis zu 6 Wochen das volle Gehalt weiter.",
    paragraphs: [
      "Die Lohnfortzahlung ist die gesetzliche Pflicht des Arbeitgebers, das volle Gehalt bei Krankheit bis zu 6 Wochen weiterzuzahlen. Voraussetzung: ununterbrochene Beschäftigung von mindestens 4 Wochen.",
      "Nach 6 Wochen übernimmt die gesetzliche Krankenkasse das Krankengeld (70 % des Bruttos, max. 90 % des Nettos) – maximal 78 Wochen wegen derselben Krankheit innerhalb von 3 Jahren.",
    ],
    related: ["krankengeld", "krankentagegeld", "aussteuerung"],
  },
  {
    slug: "marktkapitalisierung",
    term: "Marktkapitalisierung",
    category: "Investments",
    short:
      "Gesamtwert aller Aktien eines Unternehmens – Kurs mal Aktienanzahl. Bestimmt die Gewichtung in Indizes.",
    paragraphs: [
      "Die Marktkapitalisierung ist der Gesamtwert aller Aktien eines börsennotierten Unternehmens – Aktienkurs multipliziert mit der Anzahl der ausgegebenen Aktien.",
      "Sie bestimmt die Gewichtung in Indizes wie dem MSCI World oder DAX. Apple, Microsoft & Co. dominieren globale Indizes, weil ihre Marktkapitalisierung im Billionen-Bereich liegt.",
    ],
    related: ["aktie", "dax", "msci-world", "index"],
  },
  {
    slug: "mindestlohn",
    term: "Mindestlohn",
    category: "Vermögensaufbau",
    short:
      "Gesetzliche Untergrenze für Arbeitsentgelt in Deutschland – seit 01.01.2026 13,90 € pro Stunde, ab 01.01.2027 14,60 €.",
    paragraphs: [
      "Der gesetzliche Mindestlohn ist die unterste Grenze für Arbeitsentgelt in Deutschland. Seit 1. Januar 2026 beträgt er 13,90 € pro Stunde; zum 1. Januar 2027 steigt er auf 14,60 € (Vierte Mindestlohnanpassungsverordnung, BGBl. 2025 I). Er gilt für nahezu alle Beschäftigten ab 18 Jahren.",
      "Ausnahmen gibt es u. a. für Auszubildende, Praktikanten und Langzeitarbeitslose in den ersten 6 Monaten. Bei Verstößen drohen Arbeitgebern hohe Bußgelder; Nachzahlungen können bis zu 3 Jahre rückwirkend gefordert werden.",
    ],
    related: ["bruttolohn", "tarifvertrag", "nettolohn", "minijob"],
  },
  {
    slug: "nettorendite",
    term: "Nettorendite",
    category: "Investments",
    short:
      "Tatsächliche Rendite nach Steuern, Kosten und Inflation – einzig relevante Kennzahl für Anleger.",
    paragraphs: [
      "Die Nettorendite ist die tatsächliche Rendite nach Abzug von Steuern, Kosten und Inflation. Sie zeigt, was real von Deinem Anlageerfolg übrig bleibt.",
      "Beispiel: 7 % Bruttorendite minus 0,2 % TER minus 1,4 % Steuern minus 2 % Inflation ergibt rund 3,4 % reale Nettorendite. Nur diese Zahl zählt für den langfristigen Vermögenszuwachs.",
    ],
    related: ["rendite", "gesamtkostenquote", "wertentwicklung", "abgeltungssteuer"],
  },
  {
    slug: "opportunitaetskosten",
    term: "Opportunitätskosten",
    category: "Investments",
    short:
      "Entgangene Gewinne durch eine Entscheidung – z. B. Geld auf dem Girokonto statt im ETF.",
    paragraphs: [
      "Opportunitätskosten sind die entgangenen Gewinne, die durch eine Entscheidung gegen eine bessere Alternative entstehen. Wer Geld auf dem Girokonto liegen lässt, statt es zu investieren, verliert die mögliche ETF-Rendite.",
      "Beispiel: 10.000 € 30 Jahre auf dem Konto vs. im ETF (7 % p. a.) → ETF-Endwert ca. 76.000 € – Opportunitätskosten rund 66.000 €.",
    ],
    related: ["rendite", "cost-average-effekt", "zinseszinseffekt"],
  },
  {
    slug: "pflegegrad",
    term: "Pflegegrad",
    category: "Versicherung",
    short:
      "Bestimmt den Umfang der Pflegeleistungen (1–5) – die Lücke zum tatsächlichen Pflegebedarf ist groß.",
    paragraphs: [
      "Der Pflegegrad (1–5) bestimmt den Umfang der Leistungen aus der gesetzlichen Pflegeversicherung. Pflegegrad 1 deckt nur sehr geringe Einschränkungen, Pflegegrad 5 schwerste Pflegebedürftigkeit.",
      "Ab Pflegegrad 2 gibt es nennenswerte Leistungen wie Pflegegeld oder Sachleistungen. Die Differenz zu den tatsächlichen Pflegekosten ist oft mehrere Tausend Euro pro Monat – Pflegezusatzversicherungen schließen die Lücke.",
    ],
    related: ["pflegeversicherung", "pflegeluecke", "langzeitpflege"],
  },
  {
    slug: "privatrente",
    term: "Private Rentenversicherung",
    category: "Altersvorsorge",
    short:
      "Ergänzung zur gesetzlichen Rente – fondsgebunden meist deutlich renditestärker als klassische Varianten.",
    paragraphs: [
      "Eine private Rentenversicherung ist eine private Ergänzung zur gesetzlichen Rente. Beiträge in der Ansparphase, lebenslange Rente oder Kapitalauszahlung im Alter.",
      "Klassische Varianten mit Garantiezins lohnen sich kaum noch. Fondsgebundene Rentenversicherungen bieten höhere Renditechancen und steuerliche Vorteile gegenüber einem reinen ETF-Depot – vor allem bei langer Laufzeit.",
    ],
    related: ["fondsgebundene-rentenversicherung", "ueberschussbeteiligung", "garantiezins", "auszahlungsplan", "deckungskapital"],
  },
  {
    slug: "rentenversicherungsnummer",
    term: "Rentenversicherungsnummer",
    category: "Altersvorsorge",
    short:
      "Lebenslange Identifikationsnummer der gesetzlichen Rentenversicherung – steht auf Sozialversicherungsausweis.",
    paragraphs: [
      "Die Rentenversicherungsnummer ist eine lebenslange, eindeutige Identifikationsnummer für die gesetzliche Rentenversicherung. Sie wird Dir einmalig zugeteilt und ändert sich nie.",
      "Du findest sie auf Deinem Sozialversicherungsausweis, der Renteninformation, dem Rentenbescheid und der Mitgliedsbescheinigung der Krankenkasse. Bei Verlust kannst Du sie kostenlos bei der Deutschen Rentenversicherung neu anfordern.",
    ],
    related: ["gesetzliche-rentenversicherung", "rentenbescheid", "jaehrliche-renteninformation"],
  },
  {
    slug: "steuerabzug",
    term: "Steuerabzug",
    category: "Steuern",
    short:
      "Reduziert das zu versteuernde Einkommen – z. B. Werbungskosten, Sonderausgaben, Vorsorgeaufwendungen.",
    paragraphs: [
      "Ein Steuerabzug (Steuerminderung) reduziert das zu versteuernde Einkommen und damit die Steuerlast. Dazu zählen Werbungskosten, Sonderausgaben, Vorsorgeaufwendungen und außergewöhnliche Belastungen.",
      "Wer alle möglichen Abzüge nutzt, holt sich pro Jahr im Schnitt 1.000 €+ vom Finanzamt zurück. Belege aufheben und die Steuererklärung sorgfältig vorbereiten – am besten mit Steuersoftware oder Berater.",
    ],
    related: ["lohnsteuer", "kapitalertragsteuer", "abgeltungssteuer", "freistellungsauftrag"],
  },
  {
    slug: "steuerprogression",
    term: "Steuerprogression",
    category: "Steuern",
    short:
      "Höheres Einkommen wird mit höherem Steuersatz belastet – in Deutschland bis 45 % Spitzensteuersatz.",
    paragraphs: [
      "Die Steuerprogression bedeutet, dass ein höheres Einkommen mit einem höheren Steuersatz besteuert wird. In Deutschland steigt der Grenzsteuersatz von 14 % (Eingangssatz) bis 45 % (Reichensteuer).",
      "Wichtig ist die Unterscheidung zwischen Grenzsteuersatz (auf den nächsten Euro) und Durchschnittssteuersatz (über das gesamte Einkommen). Letzterer ist immer niedriger als der Grenzsteuersatz.",
    ],
    related: ["einkommensteuer", "grundfreibetrag", "progressionsvorbehalt", "lohnsteuer"],
  },
  {
    slug: "steuerstundung",
    term: "Steuerstundung",
    category: "Steuern",
    short:
      "Verschiebt die Steuerzahlung in die Zukunft – z. B. bei thesaurierenden ETFs erst beim Verkauf.",
    paragraphs: [
      "Eine Steuerstundung verschiebt die Steuerzahlung auf einen späteren Zeitpunkt. Bei thesaurierenden ETFs werden Kursgewinne erst beim Verkauf voll besteuert – jährlich fällt nur eine kleine Vorabpauschale an.",
      "Das schafft einen Zinseszinseffekt: Die noch nicht gezahlten Steuern arbeiten weiter für Dich. Über Jahrzehnte kann das mehrere Tausend Euro mehr Endvermögen bedeuten.",
    ],
    related: ["vorauszahlung-steuer", "nachgelagerte-besteuerung", "ruerup-rente"],
  },
  {
    slug: "transparente-finanzberatung",
    term: "Transparenz (Finanzberatung)",
    category: "Versicherung",
    short:
      "Berater legt seine Vergütung offen – Provisions- oder Honorarberatung. FINABLE arbeitet ohne Provisionsanreize.",
    paragraphs: [
      "Transparente Finanzberatung bedeutet, dass der Berater offenlegt, wie er vergütet wird – durch Provisionen vom Produktgeber oder durch Honorar vom Kunden.",
      "Provisionsbasierte Beratung birgt das Risiko von Fehlanreizen, da nur Produkte empfohlen werden, die Provisionen zahlen. Eine unabhängige Honorarberatung wie bei FINABLE ist frei von solchen Interessenkonflikten.",
    ],
    related: ["risikoprofil", "finanzplan", "versicherungslucke"],
  },
  {
    slug: "ueberschussbeteiligung",
    term: "Überschussbeteiligung",
    category: "Versicherung",
    short:
      "Zusätzlicher Gewinnanteil aus Versicherungen – nicht garantiert, schwankt mit Kapitalmarkt und Risikoverlauf.",
    paragraphs: [
      "Die Überschussbeteiligung ist ein zusätzlicher Gewinnanteil, den Versicherungsunternehmen aus Kapitalanlage-, Risiko- und Kostenüberschüssen an die Versicherten ausschütten.",
      "Sie ist nicht garantiert und kann jährlich schwanken. Bei klassischen Lebensversicherungen liegt sie aktuell oft im Bereich von 1–2 % zusätzlich zum Garantiezins – Tendenz fallend.",
    ],
    related: ["lebensversicherung", "privatrente", "garantiezins", "fondsgebundene-rentenversicherung", "deckungskapital"],
  },
  {
    slug: "vermoegensteuer",
    term: "Vermögensteuer",
    category: "Steuern",
    short:
      "Steuer auf privates Vermögen – in Deutschland aktuell nicht erhoben, aber politisch diskutiert.",
    paragraphs: [
      "Eine Vermögensteuer auf privates Vermögen wird in Deutschland aktuell nicht erhoben – sie ist seit 1997 ausgesetzt. In der politischen Debatte taucht sie jedoch regelmäßig wieder auf.",
      "Andere europäische Länder wie Norwegen, Spanien oder die Schweiz erheben sie. Vorbereiten lässt sich eine mögliche Wiedereinführung u. a. durch internationale Diversifikation und Steuerstrukturierung.",
    ],
    related: ["erbschaftssteuer", "einkommensteuer", "kapitalertragsteuer"],
  },
  {
    slug: "vorsorgeaufwendungen",
    term: "Vorsorgeaufwendungen",
    category: "Steuern",
    short:
      "Ausgaben für Versicherungen und Altersvorsorge – steuerlich als Sonderausgaben absetzbar.",
    paragraphs: [
      "Vorsorgeaufwendungen sind Beiträge zu bestimmten Versicherungen und Altersvorsorgeprodukten, die steuerlich als Sonderausgaben absetzbar sind – Kranken-, Pflege-, Renten-, Arbeitslosen-, Haftpflicht- und Berufsunfähigkeitsversicherung.",
      "Für die Altersvorsorge gilt 2026 ein Höchstbetrag von 30.826 € (Single) bzw. 61.652 € (Verheiratete). Sonstige Vorsorgeaufwendungen sind bis 1.900 € (Arbeitnehmer) bzw. 2.800 € (Selbstständige) absetzbar.",
    ],
    related: ["basisrente"],
  },
  {
    slug: "wartezeit-rente",
    term: "Wartezeit (Rente)",
    category: "Altersvorsorge",
    short:
      "Mindestversicherungszeit für einen gesetzlichen Rentenanspruch – Regelaltersrente: 5 Jahre.",
    paragraphs: [
      "Die Wartezeit ist die Mindestversicherungszeit, die für einen Rentenanspruch in der gesetzlichen Rentenversicherung notwendig ist. Für die Regelaltersrente sind mindestens 5 Jahre Beitragszeit erforderlich.",
      "Auch Kindererziehungszeiten, Pflege- und Wehrdienstzeiten zählen mit. Für die abschlagsfreie Altersrente nach 45 Jahren („für besonders langjährig Versicherte“) müssen entsprechend 45 Beitragsjahre vorliegen.",
    ],
    related: ["gesetzliche-rentenversicherung", "rentenpunkte", "altersrente", "rentenbescheid"],
  },
  {
    slug: "wkn",
    term: "Wertpapierkennnummer (WKN)",
    category: "Investments",
    short:
      "6-stellige deutsche Wertpapierkennnummer – international wird die 12-stellige ISIN verwendet.",
    paragraphs: [
      "Die WKN (Wertpapierkennnummer) ist eine 6-stellige alphanumerische Kennung für Wertpapiere in Deutschland. Jeder ETF, jede Aktie und jede Anleihe hat eine eindeutige WKN.",
      "International wird die 12-stellige ISIN (International Securities Identification Number) verwendet, die WKN und Länderkennung kombiniert. Beim Kauf in der Order-Maske kannst Du WKN oder ISIN angeben.",
    ],
    related: ["wertpapier", "aktie", "etf", "index"],
  },
  {
    slug: "zinswende",
    term: "Zinswende",
    category: "Investments",
    short:
      "Wechsel von Niedrig- zu Hochzinsphase – seit 2022 hat die EZB die Zinsen stark erhöht.",
    paragraphs: [
      "Die Zinswende bezeichnet den Wechsel von einer Niedrigzinsphase zu steigenden Zinsen. Seit 2022 hat die EZB die Leitzinsen stark erhöht, um die Inflation zu bekämpfen.",
      "Tagesgeld und Festgeld sind dadurch wieder attraktiver geworden, Immobilienkredite deutlich teurer. Für die Altersvorsorge bedeutet das: Klassische Garantieprodukte werden wieder etwas spannender, ETFs bleiben aber meist Top-Wahl.",
    ],
    related: ["leitzins", "zins", "inflation", "anleihe"],
  },
  {
    slug: "zugewinngemeinschaft",
    term: "Zugewinngemeinschaft",
    category: "Vermögensaufbau",
    short:
      "Gesetzlicher Güterstand der Ehe in Deutschland – Vermögen bleibt getrennt, Zugewinn wird bei Scheidung ausgeglichen.",
    paragraphs: [
      "Die Zugewinngemeinschaft ist der gesetzliche Güterstand in Deutschland, sofern kein Ehevertrag etwas anderes regelt. Das Vermögen jedes Partners bleibt während der Ehe getrennt.",
      "Bei Scheidung wird der während der Ehe erzielte Zugewinn (Endvermögen minus Anfangsvermögen) ausgeglichen. Wer mehr dazugewonnen hat, zahlt die Hälfte der Differenz an den anderen.",
    ],
    related: ["erbschaftssteuer", "nachlassplanung", "vollmacht"],
  },
  {
    slug: "zollfreigrenze",
    term: "Zollfreigrenze",
    category: "Steuern",
    short:
      "Die Zollfreigrenze war eine EU-weite Regelung die Pakete aus Drittstaaten bis zu einem Warenwert von 150 Euro von Zollgebühren befreit hat — ab Juli 2026 wurde sie abgeschafft.",
    paragraphs: [
      "Die Zollfreigrenze ist ein gesetzlich festgelegter Schwellenwert bis zu dem Waren aus Ländern außerhalb der EU ohne Erhebung von Zollgebühren eingeführt werden dürfen. Die bisherige EU-weite Zollfreigrenze von 150 Euro für Bestellungen aus Drittstaaten wie China, USA oder Türkei wurde zum 1. Juli 2026 abgeschafft.",
      "Seitdem fällt eine pauschale Zollabgabe von 3 Euro pro Warenkategorie innerhalb einer Sendung an — zusätzlich zur Einfuhrumsatzsteuer von 19 Prozent auf den Warenwert. Betroffen sind vor allem Onlinebestellungen bei Plattformen wie Temu, Shein oder Aliexpress.",
    ],
    related: ["einfuhrumsatzsteuer"],
  },
  {
    slug: "einfuhrumsatzsteuer",
    term: "Einfuhrumsatzsteuer",
    category: "Steuern",
    short:
      "Die Einfuhrumsatzsteuer ist eine Steuer auf Waren die aus Ländern außerhalb der EU eingeführt werden — sie beträgt in Deutschland 19 Prozent des Warenwertes.",
    paragraphs: [
      "Die Einfuhrumsatzsteuer (EUSt) ist eine Steuer die beim Import von Waren aus Ländern außerhalb der Europäischen Union erhoben wird. Sie entspricht der Mehrwertsteuer für importierte Waren und beträgt in Deutschland 19 Prozent des Warenwertes (7 Prozent bei ermäßigtem Steuersatz).",
      "Die Einfuhrumsatzsteuer fällt zusätzlich zu etwaigen Zollgebühren an und wird vom Zoll bei der Einreise oder Lieferung erhoben. Seit dem 1. Juli 2026 zahlen alle Online-Käufer beim Import aus Drittstaaten sowohl die neue Zollpauschale als auch die Einfuhrumsatzsteuer — unabhängig vom Warenwert.",
    ],
    related: ["zollfreigrenze"],
  },
  {
    slug: "erste-taetigkeitsstaette",
    term: "Erste Tätigkeitsstätte",
    category: "Steuern",
    short:
      "Die erste Tätigkeitsstätte ist der Ort dem ein Arbeitnehmer dauerhaft zugeordnet ist — sie bestimmt ob Fahrtkosten als Pendlerpauschale oder als Dienstreise abgesetzt werden können.",
    paragraphs: [
      "Die erste Tätigkeitsstätte ist eine ortsfeste betriebliche Einrichtung des Arbeitgebers, eines verbundenen Unternehmens oder eines Kunden, der ein Arbeitnehmer dauerhaft zugeordnet ist. Sie ist steuerlich entscheidend: Fahrten zur ersten Tätigkeitsstätte können nur mit der Entfernungspauschale (seit 2026: 0,38 Euro pro Kilometer einfache Strecke ab dem ersten Kilometer) abgesetzt werden — keine Rückfahrt, keine Verpflegungsmehraufwendungen.",
      "Ab 2027 soll die Zuordnung bereits nach 24 statt 48 Monaten dauerhaft werden. Damit ändert sich vor allem für Projektmitarbeiter, Berater und Arbeitnehmer im Außendienst die steuerliche Behandlung ihrer Fahrtkosten.",
    ],
    related: ["verpflegungsmehraufwand", "energiesteuer"],
  },
  {
    slug: "verpflegungsmehraufwand",
    term: "Verpflegungsmehraufwand",
    category: "Steuern",
    short:
      "Der Verpflegungsmehraufwand ist eine steuerliche Pauschale für Mehrausgaben bei Dienstreisen — 2026 beträgt er 14 Euro für mehr als 8 Stunden und 28 Euro für den ganzen Tag.",
    paragraphs: [
      "Der Verpflegungsmehraufwand (VMA) ist eine steuerliche Pauschale für die höheren Essen- und Getränkekosten die bei Dienstreisen anfallen. Er kann nur geltend gemacht werden wenn keine erste Tätigkeitsstätte am Einsatzort vorliegt. 2026 beträgt er 14 Euro für Abwesenheiten über 8 Stunden und 28 Euro für ganztägige Abwesenheit.",
      "Wichtig: Sobald ein Einsatzort zur ersten Tätigkeitsstätte wird, entfällt der Anspruch auf VMA vollständig. Der Arbeitgeber kann den VMA im Rahmen der gesetzlichen Pauschalen steuerfrei erstatten.",
    ],
    related: ["erste-taetigkeitsstaette"],
  },
  {
    slug: "energiesteuer",
    term: "Energiesteuer",
    category: "Steuern",
    short:
      "Die Energiesteuer ist eine staatliche Abgabe auf Kraft- und Heizstoffe wie Benzin, Diesel und Erdgas — sie ist im Energiesteuergesetz geregelt und beeinflusst direkt den Preis an der Tankstelle.",
    paragraphs: [
      "Die Energiesteuer (früher Mineralölsteuer) ist eine Verbrauchsteuer auf Energieträger wie Benzin, Diesel, Flüssiggas und Erdgas. Sie ist im Energiesteuergesetz geregelt und wird vom Bund erhoben. Der Steuersatz für Benzin beträgt 65,45 Cent pro Liter, für Diesel 47,04 Cent pro Liter.",
      "Die Energiesteuer macht einen erheblichen Teil des Kraftstoffpreises aus — zusammen mit der Mehrwertsteuer entfallen über 50 Prozent des Spritpreises auf staatliche Abgaben. Ein Tankrabatt ist eine temporäre Absenkung der Energiesteuer zur Entlastung der Verbraucher.",
    ],
    related: ["pendlerpauschale", "erste-taetigkeitsstaette", "einkommensteuer"],
  },
  {
    slug: "pflegeluecke",
    term: "Pflegelücke",
    category: "Versicherung",
    short:
      "Die Pflegelücke ist die Differenz zwischen den Leistungen der gesetzlichen Pflegeversicherung und den tatsächlichen Pflegekosten — sie beträgt heute oft 2.000 bis 3.000 Euro monatlich.",
    paragraphs: [
      "Die Pflegelücke bezeichnet die finanzielle Lücke zwischen dem was die gesetzliche Pflegeversicherung im Pflegefall leistet und den tatsächlichen Kosten für professionelle Pflege. Im stationären Pflegeheim beträgt der durchschnittliche Eigenanteil 2026 über 2.500 Euro monatlich — die Pflegeversicherung deckt nur einen Bruchteil davon ab.",
      "Die Pflegelücke wächst jährlich weil Pflegekosten schneller steigen als die Versicherungsleistungen angepasst werden. Eine Pflegezusatzversicherung oder ein aufgebautes Privatvermögen helfen die Lücke zu schließen.",
    ],
    related: ["pflegemindestlohn", "pflegegrad", "langzeitpflege"],
  },
  {
    slug: "pflegemindestlohn",
    term: "Pflegemindestlohn",
    category: "Versicherung",
    short:
      "Der Pflegemindestlohn ist ein branchenspezifischer gesetzlicher Mindestlohn für Pflegebeschäftigte — ab Juli 2026 beträgt er je nach Qualifikation zwischen 16,52 und 21,03 Euro pro Stunde.",
    paragraphs: [
      "Der Pflegemindestlohn ist ein gesetzlicher Mindestlohn speziell für Beschäftigte in der Pflege, der auf Grundlage des Arbeitnehmer-Entsendegesetzes festgesetzt wird. Er liegt deutlich über dem allgemeinen Mindestlohn und wird regelmäßig überprüft.",
      "Ab Juli 2026 beträgt er 16,52 Euro für Pflegehilfskräfte, 17,80 Euro für qualifizierte Pflegehilfskräfte und 21,03 Euro für Pflegefachkräfte. Er gilt verbindlich für alle ambulanten und stationären Pflegeeinrichtungen in Deutschland — rund 1,3 Millionen Beschäftigte profitieren davon.",
    ],
    related: ["pflegeluecke", "mindestlohn", "langzeitpflege"],
  },
  {
    slug: "rente-mit-63",
    term: "Rente mit 63",
    category: "Altersvorsorge",
    short:
      "Die Rente mit 63 ist die abschlagsfreie Altersrente für besonders langjährig Versicherte mit 45 Beitragsjahren — die Rentenkommission 2026 empfiehlt ihre Abschaffung nach 2031.",
    paragraphs: [
      "Die Rente mit 63 (offiziell: Altersrente für besonders langjährig Versicherte) ermöglicht Arbeitnehmern mit mindestens 45 Beitragsjahren ohne Abzüge vor dem gesetzlichen Regelrentenalter in Rente zu gehen. Sie wurde 2014 eingeführt und ist seither umstritten.",
      "Die Rentenkommission 2026 empfiehlt die Abschaffung als Beitrag zur Stabilisierung der Rentenfinanzen. Eine politische Entscheidung ist noch nicht gefallen — die Empfehlungen zielen auf den Zeitraum nach 2031.",
    ],
    related: ["rentenkommission", "altersvorsorge", "rentenluecke"],
  },
  {
    slug: "rentenkommission",
    term: "Rentenkommission",
    category: "Altersvorsorge",
    short:
      "Die Rentenkommission ist ein unabhängiges Expertengremium das Empfehlungen für die Reform des gesetzlichen Rentensystems erarbeitet — 2026 legte sie 33 Empfehlungen für die Zeit nach 2031 vor.",
    paragraphs: [
      "Die Rentenkommission ist ein von der Bundesregierung eingesetztes unabhängiges Gremium aus Wissenschaftlern, Sozialpartnern und weiteren Experten. Ihre Aufgabe ist es, Empfehlungen für die langfristige Stabilität des gesetzlichen Rentensystems zu erarbeiten.",
      "Im Juni 2026 legte die Kommission 33 Empfehlungen für den Zeitraum nach 2031 vor — darunter die Abschaffung der Rente mit 63 und eine Anhebung des frühestmöglichen Frührenten-Alters. Die Empfehlungen sind nicht bindend aber richtungsweisend für die Rentenpolitik.",
    ],
    related: ["rente-mit-63", "altersvorsorge", "rentenluecke"],
  },
  {
    slug: "fruehstart-rente",
    term: "Frühstart-Rente",
    category: "Förderungen",
    short:
      "Geplanter staatlicher Zuschuss von 10 € pro Monat für Kinder vom 6. bis 18. Lebensjahr in ein Altersvorsorgedepot — bis zu 1.440 € je Kind (Regierungsentwurf, Start 2027).",
    paragraphs: [
      "Die Frühstart-Rente (offiziell: Frühstartrente) ist eine geplante staatliche Förderung für die Altersvorsorge von Kindern. Laut Regierungsentwurf vom 12. August 2026 zahlt der Bund für jedes Kind mit erstem Wohnsitz in Deutschland ab Vollendung des 6. bis zur Vollendung des 18. Lebensjahres 10 Euro pro Monat in einen Standarddepot-Vertrag auf den Namen des Kindes — über 12 Jahre bis zu 1.440 Euro. Das Gesetz ist Stand September 2026 noch nicht beschlossen; geplant ist der Start am 1. Januar 2027 mit den Geburtsjahrgängen 2020 (rückwirkend ab 2026) und 2021.",
      "Eltern eröffnen das Depot bei einem zertifizierten Anbieter ihrer Wahl; die Förderung beantragt der Anbieter automatisch. Zuzahlungen von Eltern, Großeltern oder Dritten sind bis 6.840 Euro pro Jahr möglich. Eröffnen Eltern kein Depot, legt die Deutsche Bundesbank die Beiträge kollektiv an. Die Erträge bleiben bis zur Auszahlung steuerfrei, das geförderte Kapital wird grundsätzlich nicht vor dem 65. Lebensjahr ausgezahlt. Mit 18 kann das Depot als gefördertes Altersvorsorgedepot weiterlaufen.",
    ],
    related: ["standarddepot", "altersvorsorgedepot", "zinseszinseffekt", "kinderzulage"],
  },
  {
    slug: "standarddepot",
    term: "Standarddepot",
    category: "Altersvorsorge",
    short:
      "Kostengünstige Standardvariante des geförderten Altersvorsorgedepots ab 2027 — Effektivkosten gesetzlich auf 1 % pro Jahr gedeckelt.",
    paragraphs: [
      "Das Standarddepot (offiziell: Standarddepot-Vertrag Altersvorsorge) ist eine mit dem Altersvorsorgereformgesetz eingeführte Standardvariante der geförderten privaten Altersvorsorge. Es kann ab dem 1. Januar 2027 abgeschlossen werden. Alle Anbieter von Altersvorsorgeverträgen müssen es anbieten, und die Effektivkosten sind in der Ansparphase auf 1 Prozent pro Jahr begrenzt.",
      "Das Standarddepot funktioniert wie ein einfacher Sparplan mit Voreinstellungen: Der Anbieter legt vorab zwei Fonds fest, einen vorsichtigen Sicherheitsbaustein und einen chancenorientierten Renditebaustein, sodass in der Ansparphase keine eigenen Anlageentscheidungen nötig sind. Für die geplante Frühstart-Rente ist das Standarddepot das einzige zulässige Produkt; dort dürfen bis zur Volljährigkeit zusätzlich keine Abschluss- und Vertriebskosten erhoben werden.",
    ],
    related: ["altersvorsorgedepot", "fruehstart-rente", "riester-rente"],
  },
];

// Sort alphabetically once for stable rendering.
const sorted = [...entries].sort((a, b) => a.term.localeCompare(b.term, "de"));

export function getEntry(slug: string): GlossaryEntry | undefined {
  return entries.find((e) => e.slug === slug);
}

export function getRelated(slug: string): GlossaryEntry[] {
  const entry = getEntry(slug);
  if (!entry?.related) return [];
  return entry.related.map(getEntry).filter((e): e is GlossaryEntry => Boolean(e));
}

/** Group entries by their first letter (uppercased). */
export function groupedByLetter(): Record<string, GlossaryEntry[]> {
  return sorted.reduce<Record<string, GlossaryEntry[]>>((acc, entry) => {
    const letter = entry.term.charAt(0).toUpperCase();
    (acc[letter] ||= []).push(entry);
    return acc;
  }, {});
}

/** All letters that have at least one entry, in alphabetical order. */
export function activeLetters(): string[] {
  return Object.keys(groupedByLetter()).sort((a, b) => a.localeCompare(b, "de"));
}

/** Full alphabet for navigation, even letters without entries. */
export const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
